import {
  AutoComplete,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Input,
  List,
  Popconfirm,
  Row,
  Select,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
  notification,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import _ from 'lodash';
import { computed, makeObservable, observable, observe } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { PitStopList, PitstopTable, customFleets } from 'shared';
import CustomHeaderCell from 'shared/PitstopUI/PitstopTable/CustomHeaderCell';
import { currencyOrNumber } from 'shared/utils';
import {
  AppStore,
  CarStore,
  ContactStore,
  CurrentUserStore,
  ShopStore,
  TableStore,
  UserStore,
  WorkOrderStore,
} from 'stores';
import { PitstopTableCacheStore } from 'stores/CacheStore';
import styled from 'styled-components';

import AppAlert from 'components/AppAlert';
import DealershipChooser from 'components/DealershipChooser';
import FlexRow from 'components/General/FlexRow';
import CurrentFilterBadges from 'components/WorkOrder/CurrentFilterBadges';
import WorkOrderStatusByAssignedToWidget from 'components/WorkOrder/WorkOrderStatusByAssignedToWidget';
import { Logger } from 'stores/Classes';
import DeviceStore from 'stores/Classes/DeviceStore';
import { WorkOrderRepairPriorityEnum } from 'stores/Classes/WorkOrderObject';

import CustomTableColumn from 'components/Tables/CustomTableColumn';
import { WOCardItemPart } from 'components/WorkOrder/Parts/WOCardItemPart';
import WOFormFilterParts from 'components/WorkOrder/Parts/WOFormFilterParts';

import {
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  FilterFilled,
  InfoCircleOutlined,
  InfoCircleTwoTone,
  MoreOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import ExpandIcon from 'assets/icons/expand.svg?react';
import ExternalIcon from 'assets/icons/external.svg?react';
import ContactFormModal from 'components/Contacts/ContactFormModal';
import StatusWrapper from 'components/Vehicles/VehiclesTableComponents/StatusWrapper';
import WorkOrderEditShopStatusesModal from 'components/WorkOrder/WorkOrderEditShopStatusesModal';
import { EventBus } from 'stores/EventBus';
import dayjs from 'dayjs';

const { Option } = Select;
const { Option: AutoCompleteOption } = AutoComplete;
const { Text } = Typography;
const { Search } = Input;

const VENDOR_DETAILS_MODAL_ID = 'vendor-details-modal';

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${({ customPadding }) => customPadding || '25px'};
`;

const Container = styled.div`
  padding-right: 2vw;
`;

const DropdownButtonContainer = styled.div`
  .more-button-dropdown {
    min-width: 32px;
    min-height: 32px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 2px;

    i {
      font-size: 1.3rem;
      font-weight: bold;
      color: #000;
    }
  }
`;

const DeleteButton = styled(Button)`
  &&& {
    min-width: 32px;
    min-height: 32px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 2px;

    i {
      font-size: 1rem;
      font-weight: bold;
      color: #000;
    }
  }
`;

const FilterDropdown = styled.div`
  .filter-dropdown-list {
    padding: 8px 12px;
    border-bottom: solid 1px #e8e8e8;

    .filter-dropdown-list-item {
      margin-bottom: 5px;
    }
  }

  .filter-dropdown-actions {
    padding: 8px 12px;
    display: flex;
    justify-content: space-between;
  }

  &.assigned-to-dropdown {
    min-width: 300px;
    width: 300px;
  }

  &.service-task-filter-dropdown {
    .certain-search-item-count {
      color: #999;
    }

    .ant-select-dropdown {
      z-index: 9999;
    }
  }
`;

// Date Filter Component for Created At
class CreatedAtFilterDropdown extends Component {
  state = {
    currentDateRange: this.props.currentFilters.dateRange,
    customStartDate: this.props.currentFilters.startDate
      ? dayjs(this.props.currentFilters.startDate)
      : null,
    customEndDate: this.props.currentFilters.endDate
      ? dayjs(this.props.currentFilters.endDate)
      : null,
    selectedCreatedBy:
      this.props.createdByFilters.map((filter) => filter.key) || [],
  };

  componentDidUpdate(prevProps) {
    // Sync Created By selections from parent when badges are cleared/removed
    const prevKeys = (prevProps.createdByFilters || [])
      .map((f) => String(f.key))
      .join(',');
    const nextKeys = (this.props.createdByFilters || [])
      .map((f) => String(f.key))
      .join(',');
    if (prevKeys !== nextKeys) {
      this.setState({
        selectedCreatedBy: (this.props.createdByFilters || []).map(
          (f) => f.key
        ),
      });
    }
    // Sync date range from parent when cleared/changed externally
    const prevDate = JSON.stringify(prevProps.currentFilters || {});
    const nextDate = JSON.stringify(this.props.currentFilters || {});
    if (prevDate !== nextDate) {
      this.setState({
        currentDateRange: this.props.currentFilters.dateRange,
        customStartDate: this.props.currentFilters.startDate
          ? dayjs(this.props.currentFilters.startDate)
          : null,
        customEndDate: this.props.currentFilters.endDate
          ? dayjs(this.props.currentFilters.endDate)
          : null,
      });
    }
  }

  dateRangeOptions = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 30 Days', value: 'last30days' },
    { label: 'Last 90 Days', value: 'last90days' },
    { label: 'Last 180 Days', value: 'last180days' },
    { label: 'Custom Range', value: 'custom' },
  ];

  handleDateRangeSelection = (value) => {
    this.setState({ currentDateRange: value });
    if (value !== 'custom') {
      let startDate, endDate;
      const today = moment();

      switch (value) {
        case 'today':
          startDate = today.clone().startOf('day');
          endDate = today.clone().endOf('day');
          break;
        case 'yesterday':
          startDate = today.clone().subtract(1, 'day').startOf('day');
          endDate = today.clone().subtract(1, 'day').endOf('day');
          break;
        case 'last7days':
          startDate = today.clone().subtract(7, 'days').startOf('day');
          endDate = today.clone().endOf('day');
          break;
        case 'last30days':
          startDate = today.clone().subtract(30, 'days').startOf('day');
          endDate = today.clone().endOf('day');
          break;
        case 'last90days':
          startDate = today.clone().subtract(90, 'days').startOf('day');
          endDate = today.clone().endOf('day');
          break;
        case 'last180days':
          startDate = today.clone().subtract(180, 'days').startOf('day');
          endDate = today.clone().endOf('day');
          break;
        default:
          return;
      }
      this.setState({
        customStartDate: startDate,
        customEndDate: endDate,
      });
    }
  };

  handleApply = () => {
    const {
      currentDateRange,
      customStartDate,
      customEndDate,
      selectedCreatedBy,
    } = this.state;
    this.props.onApply(
      currentDateRange,
      customStartDate ? customStartDate.format('YYYY-MM-DD') : null,
      customEndDate ? customEndDate.format('YYYY-MM-DD') : null,
      selectedCreatedBy
    );
  };

  handleClear = () => {
    this.setState({
      currentDateRange: null,
      customStartDate: null,
      customEndDate: null,
      selectedCreatedBy: [],
    });
    this.props.onClear();
  };

  render() {
    const {
      currentDateRange,
      customStartDate,
      customEndDate,
      selectedCreatedBy,
    } = this.state;

    return (
      <FilterDropdown className="date-filter-dropdown" style={{ width: 350 }}>
        <div className="filter-dropdown-list">
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
            Filter by Date Range
          </div>
          {this.dateRangeOptions.map((option) => (
            <div className="filter-dropdown-list-item" key={option.value}>
              <Checkbox
                checked={currentDateRange === option.value}
                onChange={(e) => {
                  if (e.target.checked) {
                    this.handleDateRangeSelection(option.value);
                  }
                }}
              >
                {option.label}
              </Checkbox>
            </div>
          ))}
          {currentDateRange === 'custom' && (
            <div
              style={{
                padding: '8px 0',
                borderTop: '1px solid #f0f0f0',
                marginTop: 8,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <DatePicker
                  placeholder="Start Date"
                  value={customStartDate}
                  onChange={(date) => this.setState({ customStartDate: date })}
                  style={{ width: '100%', marginBottom: 8 }}
                />
                <DatePicker
                  placeholder="End Date"
                  value={customEndDate}
                  onChange={(date) => this.setState({ customEndDate: date })}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}

          {/* Created By Filter Section */}
          <div
            style={{
              padding: '8px 0',
              borderTop: '1px solid #f0f0f0',
              marginTop: 8,
            }}
          >
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
              Filter by Created By
            </div>
            <Select
              mode="multiple"
              placeholder="Select users"
              value={selectedCreatedBy}
              onChange={(values) =>
                this.setState({ selectedCreatedBy: values })
              }
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const optionText = option.children?.toString() || '';
                return (
                  optionText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {(this.props.createdByOptions || []).map((user) => (
                <Option key={user.value} value={user.value}>
                  {user.text}
                </Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="filter-dropdown-actions">
          <Button onClick={this.handleClear} size="small">
            Clear
          </Button>
          <Button type="primary" size="small" onClick={this.handleApply}>
            Apply
          </Button>
        </div>
      </FilterDropdown>
    );
  }
}

// Date Filter Component for Started At
class StartedAtFilterDropdown extends Component {
  state = {
    currentDateRange: this.props.currentFilters.dateRange,
    customStartDate: this.props.currentFilters.startDate
      ? dayjs(this.props.currentFilters.startDate)
      : null,
    customEndDate: this.props.currentFilters.endDate
      ? dayjs(this.props.currentFilters.endDate)
      : null,
  };

  componentDidUpdate(prevProps) {
    const prevDate = JSON.stringify(prevProps.currentFilters || {});
    const nextDate = JSON.stringify(this.props.currentFilters || {});
    if (prevDate !== nextDate) {
      this.setState({
        currentDateRange: this.props.currentFilters.dateRange,
        customStartDate: this.props.currentFilters.startDate
          ? dayjs(this.props.currentFilters.startDate)
          : null,
        customEndDate: this.props.currentFilters.endDate
          ? dayjs(this.props.currentFilters.endDate)
          : null,
      });
    }
  }

  dateRangeOptions = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 30 Days', value: 'last30days' },
    { label: 'Last 90 Days', value: 'last90days' },
    { label: 'Last 180 Days', value: 'last180days' },
    { label: 'Custom Range', value: 'custom' },
  ];

  handleDateRangeSelection = (value) => {
    this.setState({ currentDateRange: value });
    if (value !== 'custom') {
      let startDate, endDate;
      const today = moment();

      switch (value) {
        case 'today':
          startDate = today.clone().startOf('day');
          endDate = today.clone().endOf('day');
          break;
        case 'yesterday':
          startDate = today.clone().subtract(1, 'day').startOf('day');
          endDate = today.clone().subtract(1, 'day').endOf('day');
          break;
        case 'last7days':
          startDate = today.clone().subtract(7, 'days').startOf('day');
          endDate = today.clone().endOf('day');
          break;
        case 'last30days':
          startDate = today.clone().subtract(30, 'days').startOf('day');
          endDate = today.clone().endOf('day');
          break;
        case 'last90days':
          startDate = today.clone().subtract(90, 'days').startOf('day');
          endDate = today.clone().endOf('day');
          break;
        case 'last180days':
          startDate = today.clone().subtract(180, 'days').startOf('day');
          endDate = today.clone().endOf('day');
          break;
        default:
          return;
      }
      this.setState({
        customStartDate: startDate,
        customEndDate: endDate,
      });
    }
  };

  handleApply = () => {
    const { currentDateRange, customStartDate, customEndDate } = this.state;
    if (currentDateRange && customStartDate && customEndDate) {
      this.props.onApply(
        currentDateRange,
        customStartDate.format('YYYY-MM-DD'),
        customEndDate.format('YYYY-MM-DD')
      );
    }
  };

  handleClear = () => {
    this.setState({
      currentDateRange: null,
      customStartDate: null,
      customEndDate: null,
    });
    this.props.onClear();
  };

  render() {
    const { currentDateRange, customStartDate, customEndDate } = this.state;

    return (
      <FilterDropdown className="date-filter-dropdown" style={{ width: 300 }}>
        <div className="filter-dropdown-list">
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
            Filter by Date Range
          </div>
          {this.dateRangeOptions.map((option) => (
            <div className="filter-dropdown-list-item" key={option.value}>
              <Checkbox
                checked={currentDateRange === option.value}
                onChange={(e) => {
                  if (e.target.checked) {
                    this.handleDateRangeSelection(option.value);
                  }
                }}
              >
                {option.label}
              </Checkbox>
            </div>
          ))}
          {currentDateRange === 'custom' && (
            <div
              style={{
                padding: '8px 0',
                borderTop: '1px solid #f0f0f0',
                marginTop: 8,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <DatePicker
                  placeholder="Start Date"
                  value={customStartDate}
                  onChange={(date) => this.setState({ customStartDate: date })}
                  style={{ width: '100%', marginBottom: 8 }}
                />
                <DatePicker
                  placeholder="End Date"
                  value={customEndDate}
                  onChange={(date) => this.setState({ customEndDate: date })}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="filter-dropdown-actions">
          <Button onClick={this.handleClear} size="small">
            Clear
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={this.handleApply}
            disabled={!currentDateRange || !customStartDate || !customEndDate}
          >
            Apply
          </Button>
        </div>
      </FilterDropdown>
    );
  }
}

// Service Task Filter Component
class ServiceTaskFilterDropdown extends Component {
  state = {
    searchValue: this.props.currentFilters.searchValue || '',
    vmrsCodes: [],
    vmrs_sys_code: this.props.currentFilters.vmrs_sys_code || null,
    vmrs_asm_code: this.props.currentFilters.vmrs_asm_code || null,
    vmrs_comp_code: this.props.currentFilters.vmrs_comp_code || null,
    sysCodes: [],
    asyCodes: [],
    compCodes: [],
  };

  componentDidMount() {
    this.loadSysCodes();
    this.setState({
      searchValue: this.props.currentFilters.searchValue || '',
      vmrs_sys_code: this.props.currentFilters.vmrs_sys_code || null,
      vmrs_asm_code: this.props.currentFilters.vmrs_asm_code || null,
      vmrs_comp_code: this.props.currentFilters.vmrs_comp_code || null,
    });
  }

  loadSysCodes = async () => {
    try {
      const data = await WorkOrderStore.getVMRSSysCodes();
      this.setState({ sysCodes: data || [] });
    } catch (error) {
      console.error('Error fetching VMRS system codes:', error);
    }
  };

  loadAsyCodes = async (sysCode) => {
    if (!sysCode) {
      this.setState({
        asyCodes: [],
        vmrs_asm_code: null,
        vmrs_comp_code: null,
        compCodes: [],
      });
      return Promise.resolve();
    }
    try {
      const data = await WorkOrderStore.getVMRSAsyCodes(sysCode);
      this.setState({ asyCodes: data || [] });
      return Promise.resolve();
    } catch (error) {
      console.error('Error fetching VMRS assembly codes:', error);
      return Promise.resolve();
    }
  };

  loadCompCodes = async (sysCode, asyCode) => {
    if (!sysCode || !asyCode) {
      this.setState({ compCodes: [], vmrs_comp_code: null });
      return Promise.resolve();
    }
    try {
      const data = await WorkOrderStore.getVMRSCompCodes(sysCode, asyCode);
      this.setState({ compCodes: data || [] });
      return Promise.resolve();
    } catch (error) {
      console.error('Error fetching VMRS component codes:', error);
      return Promise.resolve();
    }
  };

  getVMRSCodes = async (value) => {
    if (!value || value.trim() === '') {
      this.setState({ vmrsCodes: [] });
      return;
    }
    try {
      const woOcrAbortController = new AbortController();
      const data = await WorkOrderStore.workOrderVMRS(
        {
          user_search_term: value,
        },
        woOcrAbortController.signal
      );
      this.setState({ vmrsCodes: data || [] });
    } catch (error) {
      console.error('Error fetching VMRS codes:', error);
      this.setState({ vmrsCodes: [] });
    }
  };

  handleSearchChange = (value) => {
    this.setState({ searchValue: value });
  };

  searchHandler = _.debounce((value) => {
    this.getVMRSCodes(value);
  }, 500);

  handleSelect = (value, option) => {
    let event = option.props.onClick;
    if (event) {
      event = (e) => {
        e.stopPropagation();
      };
    }
    this.setState({ searchValue: value });

    // Parse the VMRS code from the selected value and update dropdowns
    const vmrsCode = value.split(' ')[0];
    if (vmrsCode) {
      const parts = vmrsCode.slice(0, 12).split('-');
      const [sys, asm, comp] = parts;

      const sysCode = sys ? String(sys).padStart(3, '0') : null;
      const asyCode = asm ? String(asm).padStart(3, '0') : null;
      const compCode = comp ? String(comp).padStart(3, '0') : null;

      // Load dependent dropdowns first, then set the values
      if (sysCode) {
        this.loadAsyCodes(sysCode).then(() => {
          if (asyCode) {
            this.loadCompCodes(sysCode, asyCode).then(() => {
              // Set all values after loading the dependent data
              this.setState({
                vmrs_sys_code: sysCode,
                vmrs_asm_code: asyCode,
                vmrs_comp_code: compCode,
              });
            });
          } else {
            // Set sys and asm codes if no comp code
            this.setState({
              vmrs_sys_code: sysCode,
              vmrs_asm_code: asyCode,
              vmrs_comp_code: compCode,
            });
          }
        });
      } else {
        // Set the values directly if no sys code
        this.setState({
          vmrs_sys_code: sysCode,
          vmrs_asm_code: asyCode,
          vmrs_comp_code: compCode,
        });
      }
    }
  };

  handleSysCodeChange = (value) => {
    this.setState({ vmrs_sys_code: value });
    this.loadAsyCodes(value);
    this.updateSearchValue();
  };

  handleAsyCodeChange = (value) => {
    this.setState({ vmrs_asm_code: value });
    this.loadCompCodes(this.state.vmrs_sys_code, value);
    this.updateSearchValue();
  };

  handleCompCodeChange = (value) => {
    this.setState({ vmrs_comp_code: value });
    this.updateSearchValue();
  };

  updateSearchValue = () => {
    // Update search input based on selected dropdown values
    setTimeout(() => {
      const { vmrs_sys_code, vmrs_asm_code, vmrs_comp_code } = this.state;
      let searchValue = '';

      if (vmrs_sys_code) {
        searchValue = vmrs_sys_code;
        if (vmrs_asm_code) {
          searchValue += `-${vmrs_asm_code}`;
          if (vmrs_comp_code) {
            searchValue += `-${vmrs_comp_code}`;
          }
        }
      }

      this.setState({ searchValue });
    }, 100);
  };

  handleApply = () => {
    const { vmrs_sys_code, vmrs_asm_code, vmrs_comp_code, searchValue } =
      this.state;

    this.props.onApply(
      vmrs_sys_code,
      vmrs_asm_code,
      vmrs_comp_code,
      searchValue
    );
  };

  handleClear = () => {
    this.setState({
      searchValue: '',
      vmrsCodes: [],
      vmrs_sys_code: null,
      vmrs_asm_code: null,
      vmrs_comp_code: null,
      asyCodes: [],
      compCodes: [],
    });
    this.props.onClear();
  };

  render() {
    const {
      searchValue,
      vmrsCodes,
      vmrs_sys_code,
      vmrs_asm_code,
      vmrs_comp_code,
      sysCodes,
      asyCodes,
      compCodes,
    } = this.state;

    const options = (vmrsCodes || []).map((item) => {
      return (
        <AutoCompleteOption
          key={item.vmrs_code}
          value={`${item.vmrs_code} ${item.desc}`}
        >
          {item.vmrs_code}{' '}
          <span className="certain-search-item-count">{item.desc}</span>
        </AutoCompleteOption>
      );
    });

    return (
      <FilterDropdown
        className="service-task-filter-dropdown"
        style={{ width: 450, padding: '16px' }}
      >
        <div className="filter-dropdown-list">
          {/* Search Input */}
          <div style={{ marginBottom: 16 }}>
            <AutoComplete
              className="certain-category-search"
              dropdownClassName="certain-category-search-dropdown"
              dropdownMatchSelectWidth={true}
              style={{ width: '100%' }}
              dataSource={options}
              placeholder="Search Service Task or VMRS Code"
              optionLabelProp="value"
              onSelect={this.handleSelect}
              onSearch={this.searchHandler}
              onChange={this.handleSearchChange}
              value={searchValue}
            >
              <Input
                placeholder="Search Service Task or VMRS Code"
                style={{ width: '100%' }}
                suffix={<SearchOutlined />}
              />
            </AutoComplete>
          </div>

          {/* VMRS System Code Dropdown */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
              Filter by VMRS System Code
            </div>
            <Select
              placeholder="-"
              style={{ width: '100%' }}
              value={vmrs_sys_code}
              onChange={this.handleSysCodeChange}
              showSearch
              filterOption={(input, option) => {
                const optionText = option.children?.toString() || '';
                return (
                  optionText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {sysCodes.map((item) => (
                <Option
                  key={item.sys}
                  value={String(item.sys).padStart(3, '0')}
                >
                  {String(item.sys).padStart(3, '0')} - {item.description}
                </Option>
              ))}
            </Select>
          </div>

          {/* VMRS Assembly Code Dropdown */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
              Filter by VMRS Assembly Code
            </div>
            <Select
              placeholder="-"
              style={{ width: '100%' }}
              value={vmrs_asm_code}
              onChange={this.handleAsyCodeChange}
              showSearch
              filterOption={(input, option) => {
                const optionText = option.children?.toString() || '';
                return (
                  optionText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {asyCodes.map((item) => (
                <Option
                  key={item.asy}
                  value={String(item.asy).padStart(3, '0')}
                >
                  {String(item.asy).padStart(3, '0')} - {item.description}
                </Option>
              ))}
            </Select>
          </div>

          {/* VMRS Action Code Dropdown */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
              Filter by VMRS Action Code
            </div>
            <Select
              placeholder="-"
              style={{ width: '100%' }}
              value={vmrs_comp_code}
              onChange={this.handleCompCodeChange}
              showSearch
              filterOption={(input, option) => {
                const optionText = option.children?.toString() || '';
                return (
                  optionText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {compCodes.map((item) => (
                <Option
                  key={item.comp}
                  value={String(item.comp).padStart(3, '0')}
                >
                  {String(item.comp).padStart(3, '0')} - {item.description}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="filter-dropdown-actions">
          <Button onClick={this.handleClear} size="small">
            Clear
          </Button>
          <Button type="primary" size="small" onClick={this.handleApply}>
            Apply
          </Button>
        </div>
      </FilterDropdown>
    );
  }
}

// Add a simple indicator component for horizontal scrolling
const ScrollIndicator = styled.div`
  text-align: right;
  margin-bottom: 8px;
  color: #8c8c8c;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  svg {
    margin-left: 5px;
    animation: slideRight 1.5s infinite;
  }

  @keyframes slideRight {
    0%,
    100% {
      transform: translateX(0);
    }
    50% {
      transform: translateX(5px);
    }
  }
`;
const FullWidthRow = styled.tr`
  background: #e6f1ff;
  text-align: center;

  td {
    padding: 10px;
    color: #333;
  }
`;
// Update the StyledTableRow component to better indicate scrollability
const StyledTableRow = styled.div`
  .ant-table-row {
    cursor: default !important;
  }

  & .ant-table-content {
    transition: max-width 0.3s ease;
    max-width: ${({ isSidebarCollapsed, expandColumnSizes, tableMaxWidth }) => {
      if (expandColumnSizes) {
        return 'none';
      }
      if (tableMaxWidth) {
        return `${tableMaxWidth}px`;
      }
      if (isSidebarCollapsed) {
        return 'calc(100vw - 40px)';
      }
      return 'calc(100vw - 300px)';
    }} !important;

    /* Add a subtle shadow to indicate more content is available */
    box-shadow: inset -10px 0 10px -10px rgba(0, 0, 0, 0.15);

    /* Hide scrollbar but keep scrolling functionality */
    overflow-x: auto !important;
    scrollbar-width: none !important; /* Firefox */

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }

    /* IE and Edge */
    -ms-overflow-style: none !important;
  }

  .clickable-cell:hover {
    cursor: pointer;
  }

  .status-select {
    font-weight: 500;

    &.open {
      color: #123fbf;
    }

    &.in_progress {
      color: #1890ff;
    }

    &.completed {
      color: #52c41a;
    }

    &.closed {
      color: #000;
    }
  }

  .dropdown-menu {
    width: 120px;
  }

  .bottom-scrollbar-container {
    position: fixed;
    bottom: 0;
    background-color: #fff;
    height: 22px; /* Increased height for better visibility */
    overflow: hidden;
    border-top: 1px solid #e8e8e8;
    border-left: 1px solid #e8e8e8;
    border-right: 1px solid #e8e8e8;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
    z-index: 2;
    /* Width will be set dynamically to match table width */
    padding: 2px; /* Add some padding inside the container */
    width: ${({ tableMaxWidth }) => tableMaxWidth}px;
  }

  .bottom-scrollbar {
    overflow-x: auto;
    overflow-y: hidden;
    height: 13px; /* Increased height */
    padding-top: 2px; /* Center the scrollbar thumb vertically */

    &::-webkit-scrollbar {
      -webkit-appearance: none;
      height: 12px; /* Increased scrollbar thickness */
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 6px;
      background-color: rgba(0, 0, 0, 0.4); /* Darker for better visibility */
      -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.7);
      border: 2px solid white; /* Add border for better visibility */
    }

    &::-webkit-scrollbar-track {
      background-color: #f1f1f1; /* Light gray track */
      border-radius: 6px;
    }

    .bottom-scrollbar-content {
      height: 1px;
    }
  }
`;

const DropButton = styled(Button)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: white;
  border-color: ${({ color }) => color || 'inherit'};
  color: ${({ color }) => color || 'inherit'};
  padding: 5px 10px;
  border-radius: 6px;
`;

const StyledDiv = styled.div`
  font-size: 12px;
  padding-top: 5px;
  text-align: center;
`;

function SelectPartial({ selectedCount, onSelectAll, totalCount }) {
  return (
    <>
      {selectedCount} selected on this page.{' '}
      <a onClick={onSelectAll}>
        Select all {totalCount} applicable work orders instead.
      </a>{' '}
      (Only work orders matching the applied filters are available for
      selection)
    </>
  );
}

function SelectAll({ onClearSelection, totalCount }) {
  return (
    <>
      All {totalCount} work orders are selected.{' '}
      <a onClick={onClearSelection}>Clear selection</a>
    </>
  );
}
class WorkOrderTable extends Component {
  static propTypes = {
    history: PropTypes.object,
    onHistoryPush: PropTypes.func,
    shopId: PropTypes.number,
    isSelectionEnabled: PropTypes.bool,
    getCurrentSelectedPriorities: PropTypes.func,
    columnKeys: PropTypes.array,
    isEditable: PropTypes.bool,
    showSetupButton: PropTypes.bool,
    hideDealershipChooser: PropTypes.bool,
    hideAssignedWidget: PropTypes.bool,
    assetIdToFilterByDefault: PropTypes.number,
    expandColumnSizes: PropTypes.bool,
    isScrollSyncEnabled: PropTypes.bool,
  };

  tableStore = new TableStore();
  usersTableStore = new TableStore();
  carsTableStore = new TableStore();

  disposer = observe(ShopStore.currentShop, 'id', async () => {
    // reset table
    this.tableStore.reset();
    // reset assigned and shop related filters
    this.setState(
      {
        loadedShopWorkOrderStatuses: false,
        currentAssignedToFilters: [],
        currentCreatedByFilters: [],
        currentCreatedAtFilters: {
          dateRange: null,
          startDate: null,
          endDate: null,
        },
        currentStartedAtFilters: {
          dateRange: null,
          startDate: null,
          endDate: null,
        },
        currentServiceTaskFilters: {
          vmrs_sys_code: null,
          vmrs_asm_code: null,
          vmrs_comp_code: null,
          searchValue: '',
        },
        workOrdersGroupedByStatusAndAssignedUser: [],
      },
      async () => {
        // remove columns filters if any
        this.allColumns = this.allColumns.map((column) => {
          if (column.key === 'assigned_to') {
            return { ...column, filters: [], filteredValue: [] };
          }
          return column;
        });

        // Add invoice_number_customer column just after the invoice_number column if UFP Fleet
        this.showOrHideInvoiceNumberCustomerColumn();

        await this.loadShopUsers();
        await this.loadWorkOrdersGroupedByStatusAndAssignedUser();
        await this.loadVendors();
        await this.loadShopWorkOrderStatuses();
        this.setState({
          vendorsFilterOptions: this.state.vendors.map((vendor) => ({
            text: vendor.name,
            value: vendor.id,
          })),
        });
      }
    );
  });

  componentWillUnmount() {
    const { device } = DeviceStore;
    this.setState({
      isMobile: device === 'mobile' || device === 'tablet' ? true : false,
    });
    this.disposer();
    EventBus.$off(
      'WORK_ORDER_SHOP_STATUSES_UPDATED',
      this.loadShopWorkOrderStatusesUpdatedCb
    );
    EventBus.$off('dashboardWrapper/SIDEBAR_TOGGLED', this.sidebarToggledCb);
    EventBus.$off('workOrders:refreshTable');
    window.removeEventListener('resize', this.onResize);

    // Clean up scroll sync event listeners
    this.cleanupScrollSync();
  }

  get shopCars() {
    const cars = _.map(this.carsTableStore.data, (id) => CarStore.data.get(id));
    return cars;
  }

  state = {
    isMobile: false,
    loadedShopWorkOrderStatuses: false,
    allowManager: true,
    isSidebarCollapsed: false,
    data: [],
    vendors: [],
    vendorsFilterOptions: [],
    currentVendorFilters: [],
    shopWorkOrderStatuses: [],
    dropdownVisible: false,
    currentWorkOrderStatusDropdownVisibility: {},
    vendorDetailsModalVisible: false,
    selectedVendor: null,
    savingVendor: false,
    expandedCards: {},
    workOrdersGroupedByStatusAndAssignedUser: [],
    selectedWorkOrders: undefined,
    selectedWorkOrdersIds: [],
    timezone: 'America/New_York',
    loadingInitial: true,
    currentSearch: '',
    currentFilters: [],
    filterByStatusDropdownVisible: false,
    shopUsers: [],
    filterByAssignedToDropdownVisible: false,
    currentAssignedToFilters: [],
    currentAssignedToFilterOptions: [],
    currentCreatedByFilters: [],
    currentCreatedByFilterOptions: [],
    currentCreatedAtFilters: {
      dateRange: null,
      startDate: null,
      endDate: null,
    },
    currentStartedAtFilters: {
      dateRange: null,
      startDate: null,
      endDate: null,
    },
    currentServiceTaskFilters: {
      vmrs_sys_code: null,
      vmrs_asm_code: null,
      vmrs_comp_code: null,
      searchValue: '',
    },
    customColumns: [],
    popoverVisible: false,
    countFiltersSelected: 0,
    selectedRowKeys: [],
    filteredInfo: {
      status: [],
      assignedTo: [],
      vendor: [],
      fileTransfer: [],
      createdBy: [],
      createdAt: [],
      startedAt: [],
      serviceTask: [],
    },
    fileTransferFilters: [],
    wrapperTableMaxWidth: 0,
    selectAll: false,
    allRecordsRowOption: false,
  };

  get shopId() {
    return ShopStore.currentShop.id;
  }

  get currentFiltersApplied() {
    return [
      ...this.state.currentFilters,
      ...this.state.currentAssignedToFilters,
      ...this.state.currentVendorFilters,
      ...this.state.currentCreatedByFilters,
      ...(this.state.currentCreatedAtFilters.dateRange
        ? [{ key: 'created_at', text: 'Created At' }]
        : []),
      ...(this.state.currentStartedAtFilters.dateRange
        ? [{ key: 'started_at', text: 'Started At' }]
        : []),
      ...(this.state.currentServiceTaskFilters.searchValue ||
      this.state.currentServiceTaskFilters.vmrs_sys_code ||
      this.state.currentServiceTaskFilters.vmrs_asm_code ||
      this.state.currentServiceTaskFilters.vmrs_comp_code
        ? [{ key: 'service_task', text: 'Service Task' }]
        : []),
    ];
  }

  openVendorDetailsModal = (vendor) => {
    this.setState({ selectedVendor: vendor }, () => {
      AppStore.openModals.set(VENDOR_DETAILS_MODAL_ID, true);
    });
  };

  closeVendorDetailsModal = () => {
    this.setState({ selectedVendor: null }, () => {
      AppStore.openModals.set(VENDOR_DETAILS_MODAL_ID, false);
    });
  };

  loadShopUsers = async () => {
    const shopUsers = await UserStore.fetchUsersForTable(
      {
        offset: 0,
        limit: 250,
        filter: null,
      },
      this.usersTableStore
    );

    if (!shopUsers)
      AppStore.addError(
        'There was a problem loading shop users. Please try reloading the page.'
      );

    const filterOptions = (shopUsers || []).map((user) => ({
      text:
        `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      value: user.userId,
    }));

    this.setState({
      shopUsers,
      currentAssignedToFilterOptions: _.uniqBy(filterOptions, 'value'),
      currentCreatedByFilterOptions: _.uniqBy(filterOptions, 'value'),
    });
  };

  convertWorkOrdersGroupedToDesiredFormat = (workOrderGrouped) => {
    const formattedData = _.map(
      workOrderGrouped,
      (workOrdersAssignedToUser, userId) => {
        const user = _.find(this.state.shopUsers, { userId: parseInt(userId) });
        const fullName = user
          ? `${user.firstName} ${user.lastName}`
          : 'Unknown';

        const statusCounts = workOrdersAssignedToUser.reduce(
          (acc, workOrder) => {
            if (!acc[workOrder.status]) {
              acc[workOrder.status] = 0;
            }
            acc[workOrder.status] += parseInt(workOrder.count);
            return acc;
          },
          {}
        );

        const defaultStatusCounts = this.state.shopWorkOrderStatuses.map(
          (status) => {
            return {
              [status.key]: 0,
            };
          }
        );

        const combinedCounts = { ...defaultStatusCounts, ...statusCounts };

        const total = _.reduce(
          combinedCounts,
          (acc, value) => {
            return acc + value;
          },
          0
        );

        return {
          key: _.uniqueId(),
          assignedToId: parseInt(userId),
          assignedToName: fullName,
          ...combinedCounts,
          total: total,
        };
      }
    );

    // Sorting based on the total number of tasks
    return formattedData.sort((a, b) => b.total - a.total);
  };

  loadWorkOrdersGroupedByStatusAndAssignedUser = async () => {
    const workOrdersGroupedByStatusAndAssignedUser =
      await WorkOrderStore.workOrdersGroupedByStatusAndAssignedUser(
        ShopStore.currentShop.id
      );

    const workOrdersGroupedByUser = _.groupBy(
      workOrdersGroupedByStatusAndAssignedUser,
      'assignedTo'
    );

    const formatedWorkOrdersGrouped =
      this.convertWorkOrdersGroupedToDesiredFormat(workOrdersGroupedByUser);

    this.setState({
      workOrdersGroupedByStatusAndAssignedUser: formatedWorkOrdersGrouped,
    });
  };

  loadShopCars = async () => {
    await CarStore.getShopCarsForTable(
      {
        offset: 0,
        limit: 500,
        filter: null,
      },
      this.carsTableStore
    );
  };

  loadShopWorkOrderStatusesUpdatedCb = () => {
    this.loadShopWorkOrderStatuses();
  };

  sidebarToggledCb = (collapsed) => {
    this.setState({ isSidebarCollapsed: collapsed });
  };

  updateColumnsWithFilters = () => {
    this.allColumns = this.allColumns.map((column) => {
      if (column.key === 'status') {
        return {
          ...column,
          filters: this.state.shopWorkOrderStatuses,
          filteredValue:
            this.state.filteredInfo.status.length > 0
              ? this.state.filteredInfo.status
              : null,
        };
      }

      if (column.key === 'assigned_to') {
        return {
          ...column,
          filters: this.state.currentAssignedToFilterOptions,
          filteredValue:
            this.state.filteredInfo.assignedTo.length > 0
              ? this.state.filteredInfo.assignedTo
              : null,
        };
      }

      if (column.key === 'vendor_id') {
        return {
          ...column,
          filters: this.state.vendorsFilterOptions,
          filteredValue:
            this.state.filteredInfo.vendor.length > 0
              ? this.state.filteredInfo.vendor
              : null,
        };
      }

      if (column.key === 'current_status') {
        return {
          ...column,
          filters: this.fileTransferFilterOptions,
          filteredValue:
            this.state.filteredInfo.fileTransfer.length > 0
              ? this.state.filteredInfo.fileTransfer
              : null,
        };
      }

      return column;
    });
  };

  async componentDidMount() {
    EventBus.$on(
      'WORK_ORDER_SHOP_STATUSES_UPDATED',
      this.loadShopWorkOrderStatusesUpdatedCb
    );
    EventBus.$on('dashboardWrapper/SIDEBAR_TOGGLED', this.sidebarToggledCb);
    EventBus.$on('workOrders:refreshTable', () => {
      PitstopTableCacheStore.deleteKey(
        `${ShopStore.currentShop.id}-work-order-list`
      );
      WorkOrderStore.clearShopWorkOrdersCache(ShopStore.currentShop.id);

      // Force a refresh by resetting the table store
      this.tableStore.reset();

      setTimeout(() => {
        this.fetchData(null, this.tableStore);
      }, 500);
    });

    // Set the wrapper max width and updated it on resize
    this.onResize();
    window.addEventListener('resize', this.onResize);

    await this.load();

    // if the query params are present, update the state with the current filters applied
    const queryParams = new URLSearchParams(
      localStorage.getItem('workOrderFilters')
    );
    const formattedCurrentFilters = (stringKeys) =>
      stringKeys?.split(',').map((key) => ({
        key,
        label:
          this.state.shopWorkOrderStatuses.find(
            (option) => String(option.key) === String(key)
          )?.name || key,
      }));
    const formattedAssignedToFilters = (stringKeys) =>
      stringKeys?.split(',').map((key) => ({
        key,
        label:
          this.state.currentAssignedToFilterOptions.find(
            (option) => String(option.value) === String(key)
          )?.text || key,
      }));
    const formattedVendorFilters = (stringKeys) =>
      stringKeys?.split(',').map((key) => ({
        key,
        label:
          this.state.vendors.find((option) => String(option.id) === String(key))
            ?.name || key,
      }));
    this.setState(
      {
        currentFilters: queryParams.get('status')
          ? formattedCurrentFilters(queryParams.get('status'))
          : [],
        currentAssignedToFilters: queryParams.get('assignedTo')
          ? formattedAssignedToFilters(queryParams.get('assignedTo'))
          : [],
        currentVendorFilters: queryParams.get('vendor')
          ? formattedVendorFilters(queryParams.get('vendor'))
          : [],
        currentSearch: queryParams.get('search') || '',
      },
      () => {
        // update the table with the current filters applied
        this.onChangeCurrentFilters();
      }
    );

    this.loadWorkOrdersGroupedByStatusAndAssignedUser();

    try {
      const data = await ShopStore.getColumns('work-order');

      if (data) {
        this.setState({
          customColumns: data.columns,
        });
      }
    } catch (error) {
      console.error('Error loading columns:', error);
    }

    this.updateColumnsWithFilters();

    // Setup scroll sync after a delay to ensure table is rendered
    setTimeout(() => {
      this.setupScrollSync();
    }, 500);
  }

  componentDidUpdate(prevProps, prevState) {
    let { currentSearch, currentAssignedToFilterOptions } = this.state;
    if (currentSearch !== prevState.currentSearch) {
      this.handleSearchChange();
    }

    if (
      currentAssignedToFilterOptions !==
      prevState.currentAssignedToFilterOptions
    ) {
      this.allColumns = this.allColumns.map((column) => {
        if (column.key === 'assigned_to') {
          return {
            ...column,
            filters: currentAssignedToFilterOptions,
          };
        }
        return column;
      });
    }

    if (
      currentSearch !== prevState.currentSearch ||
      this.state.currentFilters !== prevState.currentFilters ||
      this.state.currentAssignedToFilters !==
        prevState.currentAssignedToFilters ||
      this.state.currentVendorFilters !== prevState.currentVendorFilters
    ) {
      // update the query params with the current filters applied
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set(
        'status',
        this.state.currentFilters.map((filter) => filter.key)
      );
      queryParams.set(
        'assignedTo',
        this.state.currentAssignedToFilters.map((filter) => filter.key)
      );
      queryParams.set(
        'vendor',
        this.state.currentVendorFilters.map((filter) => filter.key)
      );
      queryParams.set('search', currentSearch);

      // save it to local storage
      localStorage.setItem('workOrderFilters', queryParams.toString());

      if (this.props.history) {
        this.props.history.push({
          search: queryParams.toString(),
        });
      }
    }

    // Re-setup scroll sync when table data changes
    if (this.data.length !== prevState.data?.length) {
      setTimeout(() => {
        this.setupScrollSync();
      }, 300);
    }
  }

  searchDebouncer = null;

  // filterOptions = [];

  fileTransferOptions = {
    NO_FILE: { label: 'No File', color: 'default' },
    IN_PROGRESS: { label: 'In Progress', color: 'orange' },
    COMPLETED: { label: 'Complete', color: 'green' },
    FAILED: { label: 'Failed', color: 'red' },
  };

  fileTransferFilterOptions = Object.keys(this.fileTransferOptions).map(
    (key) => ({
      text: this.fileTransferOptions[key].label,
      value: key,
    })
  );

  handleSearchChange = () => {
    // Clears running timer and starts a new one each time the user types
    clearTimeout(this.searchDebouncer);
    this.searchDebouncer = setTimeout(() => {
      this.tableStore.reset();
    }, 500);
  };

  load = async () => {
    this.setState({ loadingInitial: true });
    await this.loadShopUsers();
    await this.loadShopWorkOrderStatuses();
    await this.loadVendors();
    this.showOrHideInvoiceNumberCustomerColumn();
    // await this.loadShopCars();
    this.setState({ loadingInitial: false });
  };

  loadVendors = async () => {
    try {
      const vendors =
        (await ContactStore.getShopContacts(ShopStore.currentShop.id)) || [];

      this.setState({
        vendors,
        vendorsFilterOptions: vendors.map((vendor) => ({
          text: vendor.name,
          value: vendor.id,
        })),
      });

      return vendors;
    } catch {
      return [];
    }
  };

  showOrHideInvoiceNumberCustomerColumn = () => {
    // Add invoice_number_customer column just after the invoice_number column if UFP Fleet
    if (
      customFleets.ufpFleet.includes(
        ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
      )
    ) {
      // check if invoice_number_customer is already in the columns
      if (
        this.allColumns.findIndex(
          (column) => column.key === 'invoice_number_customer'
        ) === -1
      ) {
        const indexOfInvoiceNumber = this.allColumns.findIndex(
          (column) => column.key === 'invoice_number'
        );
        this.allColumns = [
          ...this.allColumns.slice(0, indexOfInvoiceNumber + 1),
          {
            title: 'Invoice #',
            key: 'invoice_number_customer',
            dataIndex: 'invoice_number_customer',
            sorter: (a, b) =>
              a.invoice_number_customer - b.invoice_number_customer,
            render: (text, record) => {
              return text ? text : '-';
            },
            width: 150,
          },
          ...this.allColumns.slice(indexOfInvoiceNumber + 1),
        ];
      }
    } else {
      this.allColumns = this.allColumns.filter(
        (column) => column.key !== 'invoice_number_customer'
      );
    }
  };

  loadShopWorkOrderStatuses = async (retryTimes = 0) => {
    try {
      this.setState({ loadedShopWorkOrderStatuses: false });
      const shopWorkOrderStatuses =
        await WorkOrderStore.getShopWorkOrderStatuses(ShopStore.currentShop.id);
      this.setState({
        shopWorkOrderStatuses: _.chain(shopWorkOrderStatuses)
          .get('data', [])
          .map((status) => ({
            id: status.id,
            key: status.key,
            name: status.description,
            description: status.description,
            color: status.color,
            text: status.description,
            value: status.key,
            is_default: status.is_default,
          }))
          .value(),
      });
      this.setState({ loadedShopWorkOrderStatuses: true });
    } catch (error) {
      Logger.error(error);
      if (retryTimes < 3) {
        return this.loadShopWorkOrderStatuses(retryTimes + 1);
      }
      AppStore.addError('Failed to load shop work order statuses');
      this.setState({ loadedShopWorkOrderStatuses: true });
    }
  };

  fetchData = (filters, table) => {
    let { currentSearch } = this.state;
    if (_.isNil(filters)) {
      let { offset, limit, sort, filter } = this.tableStore;

      filters = { offset, limit, sort, filter };
    }

    // Set Sort by last created as default
    filters.sort = filters.sort ? filters.sort : '-created_by';

    if (this.props.assetIdToFilterByDefault) {
      filters.filter = {
        ...filters.filter,
        carIds: this.props.assetIdToFilterByDefault,
      };
    }

    if (currentSearch && currentSearch.length > 0) {
      filters.filter = {
        ...filters.filter,
        search: currentSearch,
      };
    }

    if (this.state.currentFilters.length > 0) {
      filters.filter = {
        ...filters.filter,
        status: this.state.currentFilters.map((filter) => filter.key),
      };
    }

    if (this.state.currentAssignedToFilters.length > 0) {
      filters.filter = {
        ...filters.filter,
        assignedTo: this.state.currentAssignedToFilters.map(
          (filter) => filter.key
        ),
      };
    }

    if (this.state.currentVendorFilters.length > 0) {
      filters.filter = {
        ...filters.filter,
        vendor: this.state.currentVendorFilters.map((filter) => filter.key),
      };
    }

    if (this.state.currentCreatedByFilters.length > 0) {
      filters.filter = {
        ...filters.filter,
        created_by: this.state.currentCreatedByFilters.map(
          (filter) => filter.key
        ),
      };
    }

    if (
      this.state.currentCreatedAtFilters.startDate &&
      this.state.currentCreatedAtFilters.endDate
    ) {
      filters.filter = {
        ...filters.filter,
        created_at_start_date: this.state.currentCreatedAtFilters.startDate,
        created_at_end_date: this.state.currentCreatedAtFilters.endDate,
      };
    }

    if (
      this.state.currentStartedAtFilters.startDate &&
      this.state.currentStartedAtFilters.endDate
    ) {
      filters.filter = {
        ...filters.filter,
        started_at_start_date: this.state.currentStartedAtFilters.startDate,
        started_at_end_date: this.state.currentStartedAtFilters.endDate,
      };
    }

    if (
      this.state.currentServiceTaskFilters.vmrs_sys_code ||
      this.state.currentServiceTaskFilters.vmrs_asm_code ||
      this.state.currentServiceTaskFilters.vmrs_comp_code
    ) {
      if (this.state.currentServiceTaskFilters.vmrs_sys_code) {
        filters.filter = {
          ...filters.filter,
          vmrs_sys_code: this.state.currentServiceTaskFilters.vmrs_sys_code,
        };
      }
      if (this.state.currentServiceTaskFilters.vmrs_asm_code) {
        filters.filter = {
          ...filters.filter,
          vmrs_asm_code: this.state.currentServiceTaskFilters.vmrs_asm_code,
        };
      }
      if (this.state.currentServiceTaskFilters.vmrs_comp_code) {
        filters.filter = {
          ...filters.filter,
          vmrs_comp_code: this.state.currentServiceTaskFilters.vmrs_comp_code,
        };
      }
    }

    if (
      this.state.filteredInfo &&
      this.state.filteredInfo.fileTransfer &&
      this.state.filteredInfo.fileTransfer.length > 0
    ) {
      filters.filter = {
        ...filters.filter,
        current_status: this.state.filteredInfo.fileTransfer,
      };
    }

    WorkOrderStore.fetchWorkOrdersForTable(
      filters,
      table,
      ShopStore.currentShop.id,
      false
    );
  };

  handleMenuClick = async ({ key, domEvent }, row) => {
    domEvent.stopPropagation();
    domEvent.preventDefault();

    switch (key) {
      case '1':
        try {
          AppStore.addLoading('Getting Work Order Report');
          await WorkOrderStore.printWorkOrder(row.id);
        } catch (error) {
          Logger.error(error);
          AppStore.addError(
            'Unknown error to generate the Work Order. Please try again later'
          );
        } finally {
          AppStore.removeLoading('Getting Work Order Report');
        }
        break;
      case '2':
        try {
          AppStore.addLoading('Getting Work Order Report');
          await WorkOrderStore.downloadWorkOrder(row.id);
        } catch (error) {
          Logger.error(error);
          AppStore.addError(
            'Unknown error to generate the Work Order. Please try again later'
          );
        } finally {
          AppStore.removeLoading('Getting Work Order Report');
        }
        break;
      default:
        break;
    }
  };

  handleStatusChange = async (newStatus, workOrder) => {
    try {
      const response = await WorkOrderStore.updateWorkOrderOnCustomEndpoint(
        {
          id: workOrder.id,
          status: newStatus,
        },
        {
          abortPrevious: false,
        }
      );

      // Check if we need to Resolve a DVIR
      if (
        newStatus === 'resolved' &&
        workOrder.type === 'driver_identified' &&
        workOrder.work_order_dvirs.length > 0
      ) {
        if (!CurrentUserStore.user.email) {
          throw new Error('An user is required to resolve the DVIR');
        }

        const samsaraUsers = await CarStore.fetchSamsaraUsers(
          ShopStore.currentShop.id
        );
        // Select the current user email if it's in the list
        const currentUserEmail = CurrentUserStore.user.email;
        const samsaraUserResolving = samsaraUsers.find(
          (user) =>
            String(user.email).toLowerCase() ===
            String(currentUserEmail).toLowerCase()
        );

        let defects = [];
        let dvirId = workOrder.work_order_dvirs[0].dvir_id;
        workOrder.work_order_dvirs.forEach((dvirDefects) => {
          defects.push({ id: dvirDefects.defect_id.toString() });
        });

        if (dvirId && defects.length > 0) {
          const defectsResolved = await CarStore.resolveDVIRDefects({
            shopId: ShopStore.currentShop.id,
            dvirId: dvirId,
            defects: defects,
            mechanicNotes: 'Resolved by Pitstop',
            resolvedBySamsaraUserId:
              samsaraUserResolving?.id || currentUserEmail,
          });
          AppStore.addNotification(
            `${defectsResolved.length} defects resolved successfully`
          );
        }
      }

      notification.success({
        message: 'Status updated',
        key: `statusUpdated${moment().format('x')}`,
        description: <Container>The work order status was updated.</Container>,
      });

      EventBus.$emit('workOrders:updateExpandedWorkOrder', response);

      this.loadWorkOrdersGroupedByStatusAndAssignedUser();
      return response;
    } catch (error) {
      Logger.error(error);
      let friendlyMsg =
        'We couldn\'t update the Work Order status at this moment, please try again later';
      return notification.error({
        message: 'Oops!',
        key: `statusUpdateError${moment().format('x')}`,
        description: <Container>{friendlyMsg}</Container>,
      });
    }
  };

  openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  get data() {
    let data = _.map(this.tableStore?.data, (id) =>
      WorkOrderStore.data.get(id)
    );

    return _.filter(data, Boolean);
  }

  filteredInfo = {};

  onChangeCurrentFilters = () => {
    // update table filter based on the filters selected
    const filters = {
      status: [],
      assignedTo: [],
      vendor: [],
      fileTransfer: [],
    };
    this.state.currentFilters.forEach((filter) => {
      filters.status.push(filter.key);
    });

    this.state.currentAssignedToFilters.forEach((filter) => {
      filters.assignedTo.push(filter.key);
    });

    this.state.currentVendorFilters.forEach((filter) => {
      filters.vendor.push(filter.key);
    });

    if (
      this.state.fileTransferFilters &&
      this.state.fileTransferFilters.length > 0
    ) {
      this.state.fileTransferFilters.forEach((filter) => {
        filters.fileTransfer.push(filter.key);
      });
    }

    this.setState({
      filteredInfo: filters,
      countFiltersSelected:
        filters.status.length +
        filters.assignedTo.length +
        filters.vendor.length +
        filters.fileTransfer.length,
    });

    // update the allColumns
    this.allColumns = this.allColumns.map((column) => {
      if (column.key === 'status') {
        return {
          ...column,
          filters: this.state.shopWorkOrderStatuses,
          filteredValue: filters.status.length > 0 ? filters.status : null,
        };
      }
      if (column.key === 'assigned_to') {
        return {
          ...column,
          filters: this.state.currentAssignedToFilterOptions,
          filteredValue:
            filters.assignedTo.length > 0 ? filters.assignedTo : null,
        };
      }
      if (column.key === 'vendor_id') {
        return {
          ...column,
          filters: this.state.vendorsFilterOptions,
          filteredValue: filters.vendor.length > 0 ? filters.vendor : null,
        };
      }
      if (column.key === 'current_status') {
        return {
          ...column,
          filters: this.fileTransferFilterOptions,
          filteredValue:
            filters.fileTransfer.length > 0 ? filters.fileTransfer : null,
        };
      }
      return column;
    });
    this.tableStore.reset();
  };

  handleStatusFilterChange = (selectedKeys, confirm) => {
    this.setState(
      {
        currentFilters: selectedKeys.map((key) => {
          const status = this.state.shopWorkOrderStatuses.find(
            (s) => s.key === key
          );
          return {
            key: key,
            label: status?.name || key,
          };
        }),
        filterByStatusDropdownVisible: false,
      },
      () => {
        confirm();
        this.onChangeCurrentFilters();
      }
    );
  };

  handleAssignedToFilterChange = (selectedKeys, confirm) => {
    this.setState(
      {
        currentAssignedToFilters: selectedKeys.map((key) => {
          const user = this.state.currentAssignedToFilterOptions.find(
            (u) => u.value === key
          );
          return {
            key: key,
            label: user?.text || key,
          };
        }),
        filterByAssignedToDropdownVisible: false,
      },
      () => {
        confirm();
        this.onChangeCurrentFilters();
      }
    );
  };

  handleVendorFilterChange = (selectedKeys, confirm) => {
    this.setState(
      {
        currentVendorFilters: selectedKeys.map((key) => ({
          key: key,
          label:
            this.state.vendorsFilterOptions.find((v) => v.value === key)
              ?.text || key,
        })),
        filterByVendorDropdownVisible: false,
      },
      () => {
        confirm();
        this.onChangeCurrentFilters();
      }
    );
  };

  handleCreatedAtFilterChange = (
    dateRange,
    startDate,
    endDate,
    selectedCreatedBy,
    confirm
  ) => {
    this.setState(
      {
        currentCreatedAtFilters: {
          dateRange,
          startDate,
          endDate,
        },
        currentCreatedByFilters: selectedCreatedBy.map((key) => ({
          key: key,
          label:
            this.state.currentCreatedByFilterOptions.find(
              (u) => u.value === key
            )?.text || key,
        })),
        filterByCreatedAtDropdownVisible: false,
      },
      () => {
        confirm();
        this.onChangeCurrentFilters();
      }
    );
  };

  handleStartedAtFilterChange = (dateRange, startDate, endDate, confirm) => {
    this.setState(
      {
        currentStartedAtFilters: {
          dateRange,
          startDate,
          endDate,
        },
        filterByStartedAtDropdownVisible: false,
      },
      () => {
        confirm();
        this.onChangeCurrentFilters();
      }
    );
  };

  handleServiceTaskFilterChange = (
    vmrs_sys_code,
    vmrs_asm_code,
    vmrs_comp_code,
    searchValue,
    confirm
  ) => {
    this.setState(
      {
        currentServiceTaskFilters: {
          vmrs_sys_code,
          vmrs_asm_code,
          vmrs_comp_code,
          searchValue,
        },
        filterByServiceTaskDropdownVisible: false,
      },
      () => {
        confirm();
        this.onChangeCurrentFilters();
      }
    );
  };

  handleReset = (clearFilters, type = 'status') => {
    if (clearFilters) clearFilters();

    if (type === 'fileTransfer') {
      this.setState(
        {
          fileTransferFilters: [],
          filterByFileTransferDropdownVisible: false,
          filteredInfo: {
            ...this.state.filteredInfo,
            fileTransfer: [],
          },
        },
        () => {
          this.onChangeCurrentFilters();
        }
      );
      return;
    }

    if (type === 'assignedTo') {
      this.setState(
        {
          currentAssignedToFilters: [],
          filterByAssignedToDropdownVisible: false,
        },
        () => {
          this.onChangeCurrentFilters();
        }
      );
      return;
    }

    if (type === 'vendor') {
      this.setState(
        {
          currentVendorFilters: [],
          filterByVendorDropdownVisible: false,
        },
        () => {
          this.onChangeCurrentFilters();
        }
      );
      return;
    }

    if (type === 'createdBy') {
      this.setState(
        {
          currentCreatedByFilters: [],
          filterByCreatedByDropdownVisible: false,
        },
        () => {
          this.onChangeCurrentFilters();
        }
      );
      return;
    }

    if (type === 'createdAt') {
      this.setState(
        {
          currentCreatedAtFilters: {
            dateRange: null,
            startDate: null,
            endDate: null,
          },
          filterByCreatedAtDropdownVisible: false,
        },
        () => {
          this.onChangeCurrentFilters();
        }
      );
      return;
    }

    if (type === 'startedAt') {
      this.setState(
        {
          currentStartedAtFilters: {
            dateRange: null,
            startDate: null,
            endDate: null,
          },
          filterByStartedAtDropdownVisible: false,
        },
        () => {
          this.onChangeCurrentFilters();
        }
      );
      return;
    }

    if (type === 'serviceTask') {
      this.setState(
        {
          currentServiceTaskFilters: {
            vmrs_sys_code: null,
            vmrs_asm_code: null,
            vmrs_comp_code: null,
            searchValue: '',
          },
          filterByServiceTaskDropdownVisible: false,
        },
        () => {
          this.onChangeCurrentFilters();
        }
      );
      return;
    }

    this.setState(
      {
        currentFilters: [],
        filterByStatusDropdownVisible: false,
      },
      () => {
        this.onChangeCurrentFilters();
      }
    );
  };

  handleAssignedToChange = async (newAssignedTo, row) => {
    try {
      const response = await WorkOrderStore.updateWorkOrderOnCustomEndpoint({
        id: row.id,
        assigned_to: newAssignedTo,
      });
      notification.success({
        message: 'Assigned to updated',
        key: `assignedToUpdated${moment().format('x')}`,
        description: (
          <Container>The work order assigned to was updated.</Container>
        ),
      });

      EventBus.$emit('workOrders:updateExpandedWorkOrder', response);

      this.loadWorkOrdersGroupedByStatusAndAssignedUser();
      return response;
    } catch (error) {
      Logger.error(error);
      let friendlyMsg =
        'We could not update the Work Order assigned to at this moment, please try again later';
      return notification.error({
        message: 'Oops!',
        key: `assignedToUpdateError${moment().format('x')}`,
        description: <Container>{friendlyMsg}</Container>,
      });
    }
  };

  toggleWorkOrderStatusDropdownVisible = (workOrderId, visible) => {
    this.setState((prevState) => ({
      currentWorkOrderStatusDropdownVisibility: {
        ...prevState.currentWorkOrderStatusDropdownVisibility,
        [workOrderId]: visible,
      },
    }));
  };

  stopPropagation = (e) => {
    if (e.stopPropagation) {
      e.stopPropagation();
    } else if (e.domEvent?.stopPropagation) {
      e.domEvent.stopPropagation();
    }
  };

  // handleStatusChange
  notificationKey = 'statusChange';
  onClickNewWorkOrderStatus = async (e, status, workOrder) => {
    this.stopPropagation(e);

    this.toggleWorkOrderStatusDropdownVisible(workOrder.id, false);

    // update the work order status
    try {
      notification.destroy(this.notificationKey);
      // notification.open({
      //   key: this.notificationKey,
      //   message: '',
      //   description: (
      //     <LoadingContainer customPadding="10px">
      //       <Spin tip="Updating status..." />
      //     </LoadingContainer>
      //   ),
      // });
      const newStatus = status.key;
      // force the front-end to change the status
      workOrder.status = newStatus;
      await this.handleStatusChange(newStatus, workOrder);
      this.fetchData();
    } catch (error) {
      AppStore.addError('Failed to update status, please try again.');
      Logger.error(error);
      return;
    } finally {
      notification.destroy(this.notificationKey);
    }
  };

  renderColumnStatusDropdown = (workOrder) => {
    if (!this.state.loadedShopWorkOrderStatuses) {
      return (
        <StatusWrapper>
          <Spin />
        </StatusWrapper>
      );
    }

    const woStatusLatest = workOrder.last_status_change;
    const formatted = woStatusLatest
      ? `Since ${moment(woStatusLatest).format(
          'MMM D, YYYY'
        )} (${moment().diff(moment(woStatusLatest), 'days')} Days)`
      : null;

    // Determine the current car status
    let currentWorkOrderStatus = this.state.shopWorkOrderStatuses.find(
      (status) => status.key === workOrder.status
    ) || {
      key: workOrder.status,
      description: 'Unknown',
      color: 'grey',
    };

    return (
      <StatusWrapper margin={'5px'}>
        <Dropdown
          trigger={['click']}
          open={
            this.state.currentWorkOrderStatusDropdownVisibility[workOrder.id]
          }
          onOpenChange={(flag) =>
            this.toggleWorkOrderStatusDropdownVisible(workOrder.id, flag)
          }
          menu={{
            className: 'status-menu',
            onClick: (e) => e.domEvent?.stopPropagation(),
            items: [
              ...this.state.shopWorkOrderStatuses.map((status) => ({
                key: status.key,
                label: (
                  <Button
                    onClick={(e) =>
                      this.onClickNewWorkOrderStatus(e, status, workOrder)
                    }
                    className="btn-status"
                    ghost
                    style={{
                      color: status.color,
                      borderColor: status.color,
                    }}
                  >
                    {status.description}
                  </Button>
                ),
                onClick: (e) =>
                  this.onClickNewWorkOrderStatus(e, status, workOrder),
              })),
              { type: 'divider' },
              {
                key: 'actions',
                label: (
                  <Button
                    block
                    icon={<EditOutlined />}
                    type="primary"
                    ghost
                    variant="outlined"
                    onClick={() => {
                      // hide the dropdown
                      this.toggleWorkOrderStatusDropdownVisible(
                        workOrder.id,
                        false
                      );
                      // open the modal
                      AppStore.openModals.set(
                        WorkOrderStore.WORK_ORDER_EDIT_SHOP_STATUSES_MODAL,
                        true
                      );
                    }}
                  >
                    Edit Statuses
                  </Button>
                ),
                onClick: this.stopPropagation,
              },
            ],
          }}
        >
          <DropButton
            onClick={(e) => e.stopPropagation()}
            color={currentWorkOrderStatus.color}
            loading={workOrder.isLoadingStatus}
            data-testid="work-order-status-button"
          >
            {currentWorkOrderStatus.description} <DownOutlined />
          </DropButton>
        </Dropdown>
        {woStatusLatest && <StyledDiv>{formatted}</StyledDiv>}
      </StatusWrapper>
    );
  };

  renderStatusFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => (
    <FilterDropdown>
      {/* For each filterOptions display a checkbox and the name of the filterOptions */}
      <div className="filter-dropdown-list">
        {this.state.shopWorkOrderStatuses.map((filter) => {
          return (
            <div className="filter-dropdown-list-item" key={filter.key}>
              <Checkbox
                checked={selectedKeys.includes(filter.key)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedKeys([...selectedKeys, filter.key]);
                  } else {
                    setSelectedKeys(
                      selectedKeys.filter((key) => key !== filter.key)
                    );
                  }
                }}
              >
                {filter.text}
              </Checkbox>
            </div>
          );
        })}
      </div>
      <div className="filter-dropdown-actions">
        <Button onClick={() => this.handleReset(clearFilters)} size="small">
          Reset
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={() => this.handleStatusFilterChange(selectedKeys, confirm)}
        >
          OK
        </Button>
      </div>
    </FilterDropdown>
  );

  handleFileTransferFilterChange = (selectedKeys, confirm) => {
    this.setState(
      {
        fileTransferFilters: selectedKeys.map((key) => ({
          key,
          label: this.fileTransferOptions[key]?.label || key,
        })),
      },
      () => {
        if (confirm) confirm();
        this.onChangeCurrentFilters();
      }
    );
  };

  fetchFilteredData = () => {
    this.tableStore.reset();
  };

  applyFileTransferFilters = (selectedKeys, confirm) => {
    this.setState(
      {
        fileTransferFilters: selectedKeys.map((key) => ({
          key,
          label: this.fileTransferOptions[key]?.label || key,
        })),
        filteredInfo: {
          ...this.state.filteredInfo,
          fileTransfer: [...selectedKeys],
        },
      },
      () => {
        if (confirm) confirm();
        this.updateColumnsWithFilters();

        this.fetchFilteredData();
      }
    );
  };

  renderFileTransferFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => (
    <FilterDropdown>
      {/* Checkbox list for filter options */}
      <div className="filter-dropdown-list">
        {this.fileTransferFilterOptions.map((filter) => (
          <div className="filter-dropdown-list-item" key={filter.value}>
            <Checkbox
              checked={selectedKeys.includes(filter.value)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedKeys([...selectedKeys, filter.value]);
                } else {
                  setSelectedKeys(
                    selectedKeys.filter((key) => key !== filter.value)
                  );
                }
              }}
            >
              {filter.text}
            </Checkbox>
          </div>
        ))}
      </div>
      {/* Action buttons */}
      <div className="filter-dropdown-actions">
        <Button
          onClick={() => this.handleReset(clearFilters, 'fileTransfer')}
          size="small"
        >
          Reset
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={() =>
            this.handleFileTransferFilterChange(selectedKeys, confirm)
          }
        >
          OK
        </Button>
      </div>
    </FilterDropdown>
  );

  renderAssignedToFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => {
    return (
      <FilterDropdown className="assigned-to-dropdown">
        {/* For each filterOptions display a checkbox and the name of the filterOptions */}
        <div
          className="filter-dropdown-list"
          style={{ maxHeight: '200px', overflowY: 'auto' }}
        >
          {this.state.currentAssignedToFilterOptions.map((user) => {
            return (
              <div className="filter-dropdown-list-item" key={user.value}>
                <Checkbox
                  checked={selectedKeys.includes(user.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedKeys([...selectedKeys, user.value]);
                    } else {
                      setSelectedKeys(
                        selectedKeys.filter((key) => key !== user.value)
                      );
                    }
                  }}
                >
                  {user.text}
                </Checkbox>
              </div>
            );
          })}
        </div>
        <div className="filter-dropdown-actions">
          <Button
            onClick={() => this.handleReset(clearFilters, 'assignedTo')}
            size="small"
          >
            Reset
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() =>
              this.handleAssignedToFilterChange(selectedKeys, confirm)
            }
          >
            OK
          </Button>
        </div>
      </FilterDropdown>
    );
  };

  renderVendorFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => {
    const options = this.state.vendorsFilterOptions || [];
    return (
      <FilterDropdown className="assigned-to-dropdown">
        {/* For each filterOptions display a checkbox and the name of the filterOptions */}
        <div
          className="filter-dropdown-list"
          style={{ maxHeight: '200px', overflowY: 'auto' }}
        >
          {options.length > 0 ? (
            options.map((vendor) => (
              <div className="filter-dropdown-list-item" key={vendor.value}>
                <Checkbox
                  checked={selectedKeys.includes(vendor.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedKeys([...selectedKeys, vendor.value]);
                    } else {
                      setSelectedKeys(
                        selectedKeys.filter((key) => key !== vendor.value)
                      );
                    }
                  }}
                >
                  {vendor.text}
                </Checkbox>
              </div>
            ))
          ) : (
            <div>No vendors available</div>
          )}
        </div>
        <div className="filter-dropdown-actions">
          <Button
            onClick={() => {
              if (clearFilters) {
                clearFilters();
                this.handleReset(clearFilters, 'vendor');
              }
            }}
            size="small"
          >
            Reset
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              this.handleVendorFilterChange(selectedKeys, confirm);
            }}
          >
            OK
          </Button>
        </div>
      </FilterDropdown>
    );
  };

  renderCreatedAtFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => {
    return (
      <CreatedAtFilterDropdown
        currentFilters={this.state.currentCreatedAtFilters}
        createdByFilters={this.state.currentCreatedByFilters}
        createdByOptions={this.state.currentCreatedByFilterOptions}
        onApply={(dateRange, startDate, endDate, selectedCreatedBy) => {
          this.handleCreatedAtFilterChange(
            dateRange,
            startDate,
            endDate,
            selectedCreatedBy,
            confirm
          );
        }}
        onClear={() => {
          this.handleReset(clearFilters, 'createdAt');
          // Also clear created by filters
          this.setState({ currentCreatedByFilters: [] }, () => {
            this.onChangeCurrentFilters();
          });
        }}
      />
    );
  };

  renderStartedAtFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => {
    return (
      <StartedAtFilterDropdown
        currentFilters={this.state.currentStartedAtFilters}
        onApply={(dateRange, startDate, endDate) => {
          this.handleStartedAtFilterChange(
            dateRange,
            startDate,
            endDate,
            confirm
          );
        }}
        onClear={() => this.handleReset(clearFilters, 'startedAt')}
      />
    );
  };

  renderServiceTaskFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => {
    return (
      <ServiceTaskFilterDropdown
        currentFilters={this.state.currentServiceTaskFilters}
        onApply={(
          vmrs_sys_code,
          vmrs_asm_code,
          vmrs_comp_code,
          searchValue
        ) => {
          this.handleServiceTaskFilterChange(
            vmrs_sys_code,
            vmrs_asm_code,
            vmrs_comp_code,
            searchValue,
            confirm
          );
        }}
        onClear={() => this.handleReset(clearFilters, 'serviceTask')}
      />
    );
  };

  renderFileTransferTag = (status) => {
    const defaultStyle = {
      backgroundColor: '#ffffff',
      color: '#000000',
      border: '1px solid #d9d9d9',
    };

    const option = this.fileTransferOptions[status] || {
      label: 'No File',
      color: 'gray',
    };

    let customStyle = {};

    switch (status) {
      case 'NO_FILE':
        customStyle = {
          backgroundColor: '#FAFAFA',
          color: '#000000',
          border: '1px solid #D9D9D9',
        };
        break;
      case 'IN_PROGRESS':
        customStyle = {
          backgroundColor: '#FFFBE6',
          color: '#FAAD14',
          border: '1px solid #FFE58F',
        };
        break;
      case 'COMPLETED':
        customStyle = {
          backgroundColor: '#f6ffed',
          color: '#52c41a',
          border: '1px solid #b7eb8f',
        };
        break;
      case 'FAILED':
        customStyle = {
          backgroundColor: '#fff2f0',
          color: '#f5222d',
          border: '1px solid #ffa39e',
        };
        break;
      default:
        customStyle = defaultStyle;
        break;
    }

    if (status === 'FAILED') {
      return (
        <Tooltip title="Unable to scan due to poor image quality or obstructions. Please manually enter details from the attached image">
          <Tag style={customStyle}>
            <span>
              <InfoCircleTwoTone
                style={{ color: '#f5222d', marginRight: '8px' }}
              />
              {option.label}
            </span>
          </Tag>
        </Tooltip>
      );
    }

    return <Tag style={customStyle}>{option.label}</Tag>;
  };

  allColumns = [
    {
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      editable: false,
      title: 'Order #',
      required: true,
      fixed: 'left',
      width: 140,
      render: (wo, row) => {
        const orderNumber = `#${row.w_ord_id ? row.w_ord_id : wo}`;
        return (
          <div
            className="clickable-cell"
            onClick={() => {
              EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
                type: 'work-order',
                data: row,
                title: `Work Order ${orderNumber}`,
                selectedCarId: row.id_car,
              });
            }}
            style={{ width: '100%' }}
          >
            <Tooltip title={orderNumber}>
              <span
                style={{
                  display: 'inline-block',
                  width: '100px',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: '#1890ff',
                  cursor: 'pointer',
                }}
                data-testid="work-order-number-link"
              >
                {orderNumber}
                <ExternalIcon
                  style={{ width: '12px', height: '12px', marginLeft: '4px' }}
                />
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: 'car_name',
      dataIndex: 'car_name',
      sorter: true,
      editable: false,
      title: 'Unit ID',
      width: 120,
      required: true,
      render: (id_car, row) => {
        const text = row.car_name || 'N/A';
        return (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              data-testid="work-order-unit-id"
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      editable: false,
      title: 'Status',
      width: 200,
      filters: this.state.shopWorkOrderStatuses,
      filterDropdown: this.renderStatusFilterDropdown,
      filteredValue: this.filteredInfo.status || '',
      // onFilter: (value, record) => record.status.includes(value),
      required: true,
      render: (status, row) => {
        return this.renderColumnStatusDropdown(row);
      },
    },
    {
      dataIndex: ['events', 'current_status'],
      key: 'current_status',
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="If you're using another work order software integrated with Pitstop, we'll sync its work orders with those in Pitstop">
            <InfoCircleOutlined
              style={{
                color: '#808080',
                marginRight: 8,
              }}
            />
          </Tooltip>
          <span>File Transfer</span>
        </div>
      ),
      width: 160,
      sorter: false,
      filters: this.fileTransferFilterOptions,
      filterDropdown: (props) =>
        this.renderFileTransferFilterDropdown({
          ...props,
          currentFilteredValues: this.state.filteredInfo?.fileTransfer || [],
        }),
      onFilter: (value, record) => {
        const status = record.events?.current_status || 'NO_FILE';
        return status === value;
      },
      render: (_, record) => {
        const status = record.events?.current_status || 'NO_FILE';
        return this.renderFileTransferTag(status);
      },
    },
    {
      key: 'priority',
      sorter: true,
      dataIndex: 'priority',
      editable: false,
      title: 'Repair Priority',
      width: 140,
      required: true,
      render: (repairPriority) => {
        return WorkOrderRepairPriorityEnum[repairPriority]
          ? WorkOrderRepairPriorityEnum[repairPriority]
          : 'N/A';
      },
    },
    {
      dataIndex: 'work_order_lines',
      key: 'work_order_lines',
      editable: false,
      title: 'Service Tasks',
      width: 240,
      required: true,
      filterDropdown: this.renderServiceTaskFilterDropdown,
      filteredValue:
        this.state.currentServiceTaskFilters.searchValue ||
        this.state.currentServiceTaskFilters.vmrs_sys_code ||
        this.state.currentServiceTaskFilters.vmrs_asm_code ||
        this.state.currentServiceTaskFilters.vmrs_comp_code
          ? ['filtered']
          : [],
      filterIcon: (filtered) => {
        return (
          <FilterFilled
            style={{
              color:
                this.state.currentServiceTaskFilters.vmrs_sys_code ||
                this.state.currentServiceTaskFilters.vmrs_asm_code ||
                this.state.currentServiceTaskFilters.vmrs_comp_code
                  ? '#1890ff'
                  : undefined,
            }}
          />
        );
      },
      render: (woLines) => {
        const getTitle = (woLine) => {
          const hasVmrsInfo =
            (woLine.vmrs_sys_code && woLine.vmrs_sys_text) ||
            (woLine.vmrs_asm_code && woLine.vmrs_asm_text) ||
            (woLine.vmrs_comp_code && woLine.vmrs_comp_text) ||
            (woLine.vmrs_full_code && woLine.vmrs_full_description);
          const code = `${woLine.vmrs_sys_code || ''}${woLine.vmrs_asm_code ? `-${woLine.vmrs_asm_code}` : ''}${woLine.vmrs_comp_code ? `-${woLine.vmrs_comp_code}` : ''}`;
          const description =
            woLine.vmrs_full_description ||
            woLine.vmrs_comp_text ||
            woLine.vmrs_asm_text ||
            woLine.vmrs_sys_text;
          if (hasVmrsInfo) {
            return `${code} - ${description}`;
          }
          return woLine?.title || '';
        };
        return woLines && woLines.length ? (
          <List
            size="small"
            dataSource={woLines ? woLines.map(getTitle) : []}
            renderItem={(item) => (
              <List.Item>
                <Tooltip title={item}>
                  <div
                    style={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item}
                  </div>
                </Tooltip>
              </List.Item>
            )}
          />
        ) : (
          'N/A'
        );
      },
    },
    {
      key: 'created_at',
      dataIndex: 'created_at',
      editable: false,
      sorter: true,
      title: 'Created At',
      width: 140,
      required: true,
      filterDropdown: this.renderCreatedAtFilterDropdown,
      filteredValue:
        this.state.currentCreatedAtFilters.dateRange ||
        this.state.currentCreatedByFilters.length > 0
          ? ['filtered']
          : [],
      filterIcon: (filtered) => {
        return (
          <FilterFilled
            style={{
              color:
                this.state.currentCreatedAtFilters.dateRange ||
                this.state.currentCreatedByFilters.length > 0
                  ? '#1890ff'
                  : undefined,
            }}
          />
        );
      },
      render: (wo, row) => {
        const user = this.state.shopUsers.find(
          (u) => u.userId === row.created_by
        );
        return (
          <Row>
            <Col span={24}>
              <Text data-testid="work-order-created-at">
                {row.created_at ? moment(row.created_at).format('ll') : '--'}
              </Text>
            </Col>
            {user && (
              <Col span={24}>
                {`${user.firstName} ${user.lastName}`.trim() || user.email}
              </Col>
            )}
          </Row>
        );
      },
    },
    {
      key: 'started_at',
      dataIndex: 'started_at',
      editable: false,
      sorter: true,
      title: 'Started At',
      width: 140,
      required: true,
      filterDropdown: this.renderStartedAtFilterDropdown,
      filteredValue: this.state.currentStartedAtFilters.dateRange
        ? ['filtered']
        : [],
      filterIcon: (filtered) => {
        return (
          <FilterFilled
            style={{
              color: this.state.currentStartedAtFilters.dateRange
                ? '#1890ff'
                : undefined,
            }}
          />
        );
      },
      render: (wo, row) => {
        return (
          <Row>
            <Col span={24}>
              <Text data-testid="work-order-started-at">
                {row.started_at ? moment(row.started_at).format('ll') : '--'}
              </Text>
            </Col>
          </Row>
        );
      },
    },

    {
      key: 'assigned_to',
      dataIndex: 'assigned_to',
      sorter: true,
      editable: false,
      title: 'Assigned To',
      width: 180,
      filters: this.state.currentAssignedToFilterOptions,
      filterDropdown: this.renderAssignedToFilterDropdown,
      // filterDropdownVisible: this.state.filterByAssignedToDropdownVisible,
      required: true,
      render: (wo, row) => {
        const options = this.state.shopUsers
          .map((user) => ({
            key: user.userId,
            value:
              `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
              user.email,
          }))
          .map((userMapped) => {
            return (
              <Option key={userMapped.key} value={userMapped.key}>
                {userMapped.value}
              </Option>
            );
          });
        return (
          <Select
            defaultValue={wo}
            style={{ width: 180 }}
            onChange={(evt) => this.handleAssignedToChange(evt, row)}
            onClick={(evt) => evt.stopPropagation()}
            data-testid="work-order-assigned-to"
          >
            {options}
          </Select>
        );
      },
    },
    {
      key: 'vendor_id',
      dataIndex: 'vendor_id',
      sorter: true,
      editable: false,
      title: 'Vendor',
      width: 180,
      required: true,
      render: (wo, row) => {
        // if no vendor, render a '-' instead of N/A
        const vendor = this.state.vendors.find((v) => v.id === row.vendor_id);
        if (!vendor) {
          return 'N/A';
        }
        // render a Link Button to open the vendor details on a modal
        // with a icon on the right
        return (
          <Tooltip title={vendor.name}>
            <Button
              type="link"
              onClick={() => this.openVendorDetailsModal(vendor)}
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
              data-testid="work-order-vendor"
            >
              {vendor.name}
              <ExpandIcon style={{ marginLeft: 4, flexShrink: 0 }} />
            </Button>
          </Tooltip>
        );
      },
      align: 'center',
      filters: this.state.vendorsFilterOptions,
      filterDropdown: this.renderVendorFilterDropdown,
    },
    {
      dataIndex: 'total_costs',
      key: 'total_costs',
      editable: false,
      title: 'Total Cost',
      width: 120,
      required: true,
      render: (totalCosts, row) => {
        return (
          <span data-testid="work-order-total-cost">
            {currencyOrNumber(_.get(row, 'costsTotal', 0))}
          </span>
        );
        // return getWorkOrderTotalCostWithTaxAndMisc({
        //   workOrderLines: row.work_order_lines,
        //   taxObj: { tax: row.tax || 0, type: row.tax_type || 'percentage' },
        //   miscObj: { misc: row.misc || 0, type: row.misc_type || 'percentage' },
        //   applyCurrency: true,
        //   workOrder: row,
        // });
      },
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      editable: false,
      title: '',
      width: 100,
      fixed: 'right',
      required: false,
      render: (_, row) => {
        return (
          <FlexRow>
            <Dropdown
              menu={{
                onClick: (evt) => this.handleMenuClick(evt, row),
                items: [
                  {
                    key: '1',
                    icon: <PrinterOutlined />,
                    label: 'Print work order',
                  },
                  {
                    key: '2',
                    icon: <DownloadOutlined />,
                    label: 'Download work order',
                  },
                ],
              }}
            >
              <DropdownButtonContainer>
                <Button
                  shape="circle"
                  size="small"
                  className="more-button-dropdown"
                >
                  <MoreOutlined />
                </Button>
              </DropdownButtonContainer>
            </Dropdown>
            {CurrentUserStore.allowManager && (
              <Popconfirm
                title="Are you sure you want to delete this work order?"
                onConfirm={() => this.onDelete(row)}
              >
                <DeleteButton shape="circle">
                  <DeleteOutlined />
                </DeleteButton>
              </Popconfirm>
            )}
          </FlexRow>
        );
      },
    },
  ];

  transformColumnsToNotEditable = (rows, propIsEditable) => {
    const isEditable = _.isNil(propIsEditable) ? true : propIsEditable;
    if (isEditable === false) {
      return _.map(rows, (row) => {
        return { ...row, editable: false };
      });
    } else {
      return rows;
    }
  };

  generateColumns = (propsColumnKeys) => {
    if (_.isEmpty(propsColumnKeys)) {
      return this.transformColumnsToNotEditable(
        this.allColumns,
        this.props.isEditable
      );
    }
    const filteredColumns = _.filter(this.allColumns, (column) => {
      return (
        _.includes(propsColumnKeys, column.key) ||
        _.includes(propsColumnKeys, column.dataIndex)
      );
    });
    return this.transformColumnsToNotEditable(
      filteredColumns,
      this.props.isEditable
    );
  };

  onEdit = async ({ newValue: updatedUser }) => {
    if (updatedUser) {
      await WorkOrderStore.updateWorkOrder(updatedUser.id, updatedUser);
    }

    this.setState({
      selectedWorkOrdersIds: [updatedUser.id],
    });
  };

  onDelete = async (workOrder) => {
    AppStore.addLoading('Deleting Work Order');
    await WorkOrderStore.deleteWorkOrder(workOrder.id);
    AppStore.removeLoading('Deleting Work Order');
    message.success('Work Order deleted successfully');
    this.tableStore.reset();
  };

  onSelectWorkOrders = (workOrderIds) => {
    this.setState({
      selectedWorkOrdersIds: workOrderIds,
    });
  };

  handlerCustomColumnTable = async (data) => {
    try {
      AppStore.addLoading('Setting Table Columns');
      const settings = await ShopStore.getShopSettings(this.shopId);

      let tables = _.get(settings.result, '[0].settings.table', []);

      let currentTable = _.find(tables, { name: data.name });

      if (currentTable) {
        currentTable.columns = data.columns;
      } else {
        tables.push(data);
      }

      _.set(settings.result, '[0].settings.table', tables);

      await ShopStore.upsertShopSettings(
        this.shopId,
        settings.result[0].settings
      );

      this.setState({
        customColumns: data.columns,
      });
      AppStore.addSuccess('Saved successfully!');
    } catch (e) {
      // Log error silently
      console.error('Error setting table columns:', e);
    } finally {
      AppStore.removeLoading('');
    }
  };

  handlerResetCustomColumnTable = async (data) => {
    try {
      AppStore.addLoading('Setting Table Columns');
      // get the shop settings
      const settings = await ShopStore.getShopSettings(this.shopId);

      if (!_.get(settings, 'result[0].settings.table')) {
        _.set(settings, 'result[0].settings.table', []);
      }

      // get the table settings
      let tableSettings = _.get(settings.result, '[0].settings.table', []);

      // find the work order table settings
      let currentTableIndex = _.findIndex(tableSettings, { name: data.name });
      if (currentTableIndex !== -1) {
        tableSettings.splice(currentTableIndex, 1);
      } else {
        tableSettings.push({ name: data.name, columns: [] });
      }

      _.set(settings, 'result[0].settings.table', tableSettings);

      await ShopStore.upsertShopSettings(
        this.shopId,
        settings.result[0].settings
      );

      this.setState({ customColumns: [] });

      AppStore.addSuccess('Saved successfully!');
    } catch (e) {
      // Log error silently
      console.error('Error resetting table columns:', e);
    } finally {
      AppStore.removeLoading('');
    }
  };

  getWorkOrderLabelFromKey = (key) => {
    const statusObj = this.state.shopWorkOrderStatuses.find(
      (s) => s.key === key
    );
    return statusObj?.name || key;
  };

  selectAll = () => {
    this.setState({
      selectAll: true,
      selectedWorkOrdersIds: this.data.map((wo) => wo.id),
    });
  };

  clearSelection = () => {
    this.setState({
      selectAll: false,
      allRecordsRowOption: false,
      selectedWorkOrdersIds: [],
    });
  };

  onSelectChange = (selectedRowKeys) => {
    this.setState({ selectedWorkOrdersIds: selectedRowKeys });
    if (selectedRowKeys.length < 10) {
      this.setState({
        selectAll: false,
        allRecordsRowOption: false,
      });
    } else {
      this.setState({
        allRecordsRowOption: true,
      });
    }
  };

  handleExportCSV = async () => {
    const { selectedWorkOrdersIds, selectAll } = this.state;

    if (
      !selectAll &&
      (!selectedWorkOrdersIds || selectedWorkOrdersIds.length === 0)
    ) {
      message.warning('No rows selected for export');
      return;
    }

    let { queryString } = WorkOrderStore.filtersUsed;
    console.log('Filters queryString:', queryString);

    console.log(
      'Filters used:',
      JSON.stringify(WorkOrderStore.filtersUsed, null, 2)
    );
    if (selectAll) {
      const url = new URL(queryString);
      url.searchParams.set('limit', '10000');
      queryString = url.toString();
    }

    const hideLoadingMessage = message.loading('Generating CSV...', 0);

    try {
      const { data: csvData } =
        await WorkOrderStore.fetchWorkOrdersForCSV(queryString);
      // console.log('CSV Data:', csvData);

      // Filter the csvData to include only the selected rows if less than 10 are selected
      const selectedData = selectAll
        ? csvData
        : csvData.filter((row) => selectedWorkOrdersIds.includes(row.id));

      const processedData = selectedData.map((row) => {
        const assignedUser = this.state.shopUsers.find(
          (user) => user.userId === row.assigned_to
        );
        const assignedTo = assignedUser
          ? `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim() ||
            assignedUser.email
          : 'N/A';

        const vendor = this.state.vendors.find((v) => v.id === row.vendor_id);
        const vendorName = vendor ? vendor.name : 'N/A';

        const priority = WorkOrderRepairPriorityEnum[row.priority]
          ? WorkOrderRepairPriorityEnum[row.priority]
          : 'N/A';

        const status = this.state.shopWorkOrderStatuses.find(
          (status) => status.key === row.status
        );
        const statusDescription = status ? status.description : 'Unknown';

        return {
          id: row.id,
          id_car: row.id_car,
          order: row.invoice_number,
          asset_type: row.asset_type,
          car_name: row.car_name,
          status: statusDescription,
          location_code: row.location_code,
          priority: priority,
          vendor_name: vendorName,
          started_at: row.started_at,
          created_at: row.created_at,
          updated_at: row.updated_at,
          service_tasks: row.work_order_lines[0]?.title || '',
          partsTotal: row.partsTotal,
          costsTotal: row.costsTotal,
          laborsTotal: row.laborsTotal,
          assigned_to: assignedTo,
        };
      });

      const csvContent = [
        'Id,Id Car, Order No.,Asset Type,Car Name,Status,Location Code,Priority,Vendor Name,Started At,Created At,Updated At,Service Tasks,Parts Total,Costs Total,Labors Total,Assigned To', // Header row
        ...processedData.map(
          (row) =>
            `"${row.id}","${row.id_car}","${row.order}","${row.asset_type}","${row.car_name}","${row.status}","${row.location_code}","${row.priority}","${row.vendor_name}","${row.started_at}","${row.created_at}","${row.updated_at}","${row.service_tasks}","${row.partsTotal}","${row.costsTotal}","${row.laborsTotal}","${row.assigned_to}"`
        ), // Data rows
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const timestamp = moment().format('YYYYMMDD_HHmmss');
      link.setAttribute('download', `export_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    } finally {
      hideLoadingMessage();
    }
  };

  onResize = () => {
    // Force the WrapperTable max-width to be the same as the <footer> element
    const footer = document.querySelector('footer');
    if (footer) {
      this.setState({ wrapperTableMaxWidth: footer.offsetWidth - 50 }, () => {
        // Update scroll sync on resize
        setTimeout(() => {
          this.setupScrollSync();
        }, 100);
      });
    }
  };

  render() {
    let newColumns = this.allColumns;
    const { selectedRowKeys } = this.state;

    const { selectedWorkOrdersIds, selectAll, allRecordsRowOption } =
      this.state;

    const rowSelection = {
      selectedRowKeys: selectAll
        ? this.data.map((wo) => wo.id)
        : selectedWorkOrdersIds,
      onChange: this.onSelectChange,
      onSelectAll: (selected, selectedRows) => {
        this.setState({
          allRecordsRowOption: selected,
          selectAll: false,
        });
      },
    };

    const { device, orientation } = DeviceStore;
    this.backupColumns = this.allColumns;
    if (device === 'tablet' && orientation === 'portrait') {
      const allowedColumns = [
        'invoice_number',
        'car_name',
        'status',
        'file_transfer',
        'created_at',
        'started_at',
        'assigned_to',
        'actions',
      ];
      newColumns = this.allColumns
        .filter(
          (c) =>
            allowedColumns.includes(c.key) ||
            ['invoice_number', 'actions'].includes(c.dataIndex)
        )
        .map((col) => {
          return {
            ...col,
            ...{
              editable: false,
              sorter: false,
            },
          };
        });
    } else {
      if (this.state.customColumns.length > 0) {
        newColumns = this.allColumns.filter((col) =>
          this.state.customColumns.includes(col.key)
        );
      }
    }

    return this.state.loadingInitial ? (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    ) : (
      <>
        {!this.props.hideDealershipChooser && <DealershipChooser />}

        {device === 'mobile' && (
          <Row style={{ marginBottom: 10, marginTop: 10 }} gutter={[16, 16]}>
            <Col span={10}>
              <WOFormFilterParts
                optionStatus={this.state.shopWorkOrderStatuses.map(
                  (workOrderStatus) => ({
                    value: workOrderStatus.key,
                    title: workOrderStatus.name,
                  })
                )}
                optionAssigned={this.state.shopUsers.map((user) => {
                  return {
                    value: user.userId,
                    title:
                      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                      user.email,
                  };
                })}
                changeFilter={this.handleChangeFormFilter}
                popoverVisible={this.state.popoverVisible}
                togglePopoverVisibility={this.togglePopoverVisibility}
                onClear={this.onClearAllFilters}
                countFiltersSelected={
                  this.state.countFiltersSelected
                    ? this.state.countFiltersSelected + 1
                    : 0
                }
              />
            </Col>
            <Col span={14}>
              <Search
                placeholder={
                  customFleets.ufpFleet.includes(
                    ShopStore.currentShop.parentShopId ||
                      ShopStore.currentShop.id
                  )
                    ? 'Search by Order # or Invoice # or Unit ID'
                    : 'Search by Order # or Unit ID'
                }
                value={this.state.currentSearch}
                onChange={(event) => {
                  this.setState({
                    currentSearch: event.target.value,
                  });
                }}
                allowClear
                onClear={(event) => {
                  this.setState({
                    currentSearch: '',
                  });
                }}
              />
            </Col>
          </Row>
        )}
        {/* Add widget with WorkOrder grouped by assignedTo and status */}
        {!this.props.hideAssignedWidget &&
          this.state.workOrdersGroupedByStatusAndAssignedUser.length > 0 && (
            <Row style={{ marginBottom: 10, marginTop: 10 }} gutter={[16, 16]}>
              <Col span={device === 'mobile' || device === 'tablet' ? 24 : 24}>
                <WorkOrderStatusByAssignedToWidget
                  workOrdersGroupedByStatusAndAssignedUser={
                    this.state.workOrdersGroupedByStatusAndAssignedUser
                  }
                  onClickElement={(type, value, row) => () => {
                    if (type === 'user') {
                      this.setState(
                        {
                          currentAssignedToFilters: [
                            {
                              key: row.assignedToId,
                              label: row.assignedToName,
                            },
                          ],
                          currentFilters: [],
                        },
                        () => {
                          this.onChangeCurrentFilters();
                        }
                      );
                    } else if (type === 'onlystatus') {
                      const statusObj = this.state.shopWorkOrderStatuses.find(
                        (s) => s.key === value
                      );
                      this.setState(
                        {
                          currentFilters: [
                            {
                              key: value,
                              label: statusObj.name,
                            },
                          ],
                        },
                        () => {
                          this.onChangeCurrentFilters();
                        }
                      );
                    } else if (type === 'status') {
                      const statusObj = this.state.shopWorkOrderStatuses.find(
                        (s) => s.key === value
                      );
                      this.setState(
                        {
                          currentAssignedToFilters: [
                            {
                              key: row.assignedToId,
                              label: row.assignedToName,
                            },
                          ],
                          currentFilters: [
                            {
                              key: value,
                              label: statusObj.name,
                            },
                          ],
                        },
                        () => {
                          this.onChangeCurrentFilters();
                        }
                      );
                    }
                  }}
                />
              </Col>
            </Row>
          )}
        {device !== 'mobile' && (
          <Row
            type="flex"
            style={{ marginBottom: 10, marginTop: 10 }}
            gutter={[16, 16]}
            justify="space-between"
          >
            <Col span={6}>
              <Search
                style={{ width: '100%' }}
                placeholder={
                  customFleets.ufpFleet.includes(
                    ShopStore.currentShop.parentShopId ||
                      ShopStore.currentShop.id
                  )
                    ? 'Search by Order # or Invoice # or Unit ID'
                    : 'Search by Order # or Unit ID'
                }
                value={this.state.currentSearch}
                onChange={(event) => {
                  this.setState({
                    currentSearch: event.target.value,
                  });
                }}
                allowClear
                onClear={(event) => {
                  this.setState({
                    currentSearch: '',
                  });
                }}
              />
            </Col>

            <Col>
              <CustomTableColumn
                title={'Customize work order'}
                shopId={this.shopId}
                key={`custom-table-${new Date()}`}
                tableName="work-order"
                fixedColumns={['invoice_number', 'actions']}
                columns={this.allColumns}
                defaultColumns={this.state.customColumns}
                onSubmit={this.handlerCustomColumnTable}
                onReset={this.handlerResetCustomColumnTable}
              />
            </Col>
          </Row>
        )}

        {/* Filter badges */}
        {device !== 'mobile' && (
          <Row type="flex">
            <Col>
              <CurrentFilterBadges
                currentFiltersApplied={this.currentFiltersApplied}
                onClearAllFilters={() => {
                  this.setState(
                    {
                      currentFilters: [],
                      currentAssignedToFilters: [],
                      currentVendorFilters: [],
                      currentCreatedByFilters: [],
                      currentCreatedAtFilters: {
                        dateRange: null,
                        startDate: null,
                        endDate: null,
                      },
                      currentStartedAtFilters: {
                        dateRange: null,
                        startDate: null,
                        endDate: null,
                      },
                      currentServiceTaskFilters: {
                        vmrs_sys_code: null,
                        vmrs_asm_code: null,
                        vmrs_comp_code: null,
                        searchValue: '',
                      },
                    },
                    () => {
                      this.onChangeCurrentFilters();
                    }
                  );
                }}
                onCloseTag={(filter) => {
                  // Handle service task filter closure - clear all service task filters
                  let updatedServiceTaskFilters = {
                    ...this.state.currentServiceTaskFilters,
                  };

                  if (filter.key === 'service_task') {
                    updatedServiceTaskFilters = {
                      vmrs_sys_code: null,
                      vmrs_asm_code: null,
                      vmrs_comp_code: null,
                      searchValue: '',
                    };
                  }

                  this.setState(
                    {
                      currentFilters: this.state.currentFilters.filter(
                        (f) => f.key !== filter.key
                      ),
                      currentAssignedToFilters:
                        this.state.currentAssignedToFilters.filter(
                          (f) => f.key !== filter.key
                        ),
                      currentVendorFilters:
                        this.state.currentVendorFilters.filter(
                          (f) => f.key !== filter.key
                        ),
                      currentCreatedByFilters:
                        this.state.currentCreatedByFilters.filter(
                          (f) => f.key !== filter.key
                        ),
                      currentCreatedAtFilters:
                        filter.key === 'created_at'
                          ? { dateRange: null, startDate: null, endDate: null }
                          : this.state.currentCreatedAtFilters,
                      currentStartedAtFilters:
                        filter.key === 'started_at'
                          ? { dateRange: null, startDate: null, endDate: null }
                          : this.state.currentStartedAtFilters,
                      currentServiceTaskFilters: updatedServiceTaskFilters,
                    },
                    () => {
                      this.onChangeCurrentFilters();
                    }
                  );
                }}
              />
            </Col>
            <Col>
              <Button
                type="primary"
                disabled={
                  !this.state.selectAll &&
                  (!this.state.selectedWorkOrdersIds ||
                    this.state.selectedWorkOrdersIds.length === 0)
                }
                size="small"
                onClick={this.handleExportCSV}
              >
                Export CSV
              </Button>
            </Col>
          </Row>
        )}

        {device === 'tablet' && orientation === 'portrait' ? (
          <Row style={{ marginBottom: 24 }}>
            <Col span={24}>
              <>
                <AppAlert
                  message={
                    <>
                      <Text strong>Some columns are not shown</Text>
                      <br />
                      <Text>
                        To view all columns, use the device in landscape
                        (horizontally)
                      </Text>
                    </>
                  }
                />
              </>
            </Col>
          </Row>
        ) : (
          <></>
        )}

        {device === 'mobile' ? (
          <>
            <PitStopList
              tableStore={this.tableStore}
              id="my-list"
              data={this.data}
              getTableStore={(tableStore) => (this.tableStore = tableStore)}
              rowKey="id"
              hideTopPagination={true}
              renderItem={(item) => (
                <WOCardItemPart
                  data={item}
                  toggleCard={this.toggleCardExpansion}
                  isExpanded={this.state.expandedCards[item.id]}
                  optionStatus={this.state.shopWorkOrderStatuses.map(
                    (workOrderStatus) => ({
                      key: workOrderStatus.key,
                      value: workOrderStatus.name,
                    })
                  )}
                  changeStatus={this.handleStatusChange}
                  optionAssigned={this.state.shopUsers.map((user) => {
                    return {
                      key: user.userId,
                      value:
                        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                        user.email,
                    };
                  })}
                  changeAssigned={this.handleAssignedToChange}
                  history={this.props.history}
                />
              )}
              pagination={{
                limit: 10,
              }}
              fetchData={this.fetchData}
            />
          </>
        ) : (
          <>
            {/* Add the horizontal scroll indicator */}
            <ScrollIndicator>
              Scroll horizontally to see more
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="#8c8c8c"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ScrollIndicator>

            <StyledTableRow
              ref={this.tableRef}
              isSidebarCollapsed={this.state.isSidebarCollapsed}
              expandColumnSizes={this.props.expandColumnSizes}
              tableMaxWidth={this.state.wrapperTableMaxWidth}
            >
              <PitstopTable
                id={`${ShopStore.currentShop.id}-work-order-list`}
                data={this.data}
                rowKey="id"
                onRowClick={(record, evt) => {
                  if (evt.target.closest('.order-number-column')) {
                    if (
                      evt.target.classList.contains(
                        'ant-select-selection__rendered'
                      ) ||
                      evt.target.classList.contains(
                        'ant-select-dropdown-menu-item'
                      ) ||
                      evt.target.classList.contains(
                        'ant-select-selection-selected-value'
                      )
                    ) {
                      evt.stopPropagation();
                      return;
                    }
                    return (
                      this.props.history &&
                      this.props.history.push(`/work-order/${record.id}/edit`)
                    );
                  }
                }}
                columns={newColumns}
                hideTopPagination={true}
                fetchData={this.fetchData}
                getTableStore={(tableStore) => (this.tableStore = tableStore)}
                // components={{
                //   header: {
                //     cell: CustomHeaderCell,
                //   },
                // }}
                rowSelection={rowSelection}
                components={{
                  header: {
                    cell: CustomHeaderCell,
                  },
                  body: {
                    wrapper: (props) => (
                      <tbody {...props}>
                        {allRecordsRowOption &&
                          selectedWorkOrdersIds?.length === 10 && (
                            <FullWidthRow>
                              <td colSpan={newColumns?.length + 1}>
                                {selectAll ? (
                                  <SelectAll
                                    totalCount={this.tableStore.count}
                                    onClearSelection={this.clearSelection}
                                  />
                                ) : (
                                  <SelectPartial
                                    totalCount={this.tableStore.count}
                                    selectedCount={
                                      selectedWorkOrdersIds?.length
                                    }
                                    onSelectAll={this.selectAll}
                                  />
                                )}
                              </td>
                            </FullWidthRow>
                          )}
                        {props.children}
                      </tbody>
                    ),
                  },
                }}
                onDelete={this.onDelete}
                scroll={{ x: 2000, y: undefined }}
              />
              {this.props.isScrollSyncEnabled && (
                <div
                  className="bottom-scrollbar-container"
                  ref={this.bottomContainerRef}
                >
                  <div className="bottom-scrollbar" ref={this.bottomScrollRef}>
                    <div className="bottom-scrollbar-content"></div>
                  </div>
                </div>
              )}
            </StyledTableRow>
          </>
        )}

        <ContactFormModal
          modalId={VENDOR_DETAILS_MODAL_ID}
          saving={this.state.savingVendor}
          title="Vendor Details"
          onCancel={this.closeVendorDetailsModal}
          defaultData={this.state.selectedVendor}
          onSubmit={() => {}}
          hideFooter={true}
          disableFields={['*']}
        />

        <WorkOrderEditShopStatusesModal
          shopId={ShopStore.currentShop.id}
          isSavingStatuses={false}
          saveNewShopStatuses={() => {}}
          onCancel={() => {}}
          dataSource={this.state.shopWorkOrderStatuses.map((status) => ({
            id: status.id,
            key: status.key,
            color: status.color,
            description: status.name,
            is_default: status.is_default,
          }))}
          selectStatusAsDefault={() => {}}
          deleteStatus={() => {}}
        />
      </>
    );
  }
  constructor() {
    super();
    makeObservable(this, {
      tableStore: observable,
      data: computed,
      shopId: computed,
      shopCars: computed,
    });

    // Initialize refs properly
    this.tableRef = React.createRef();
    this.bottomScrollRef = React.createRef();
    this.bottomContainerRef = React.createRef();
  }

  // Restore the methods that were accidentally deleted
  toggleCardExpansion = (invoice_number) => {
    this.setState((prevState) => ({
      expandedCards: {
        ...prevState.expandedCards,
        [invoice_number]: !prevState.expandedCards[invoice_number],
      },
    }));
  };

  handleChangeFormFilter = (key, value) => {
    if (key === 'status') {
      this.setState({
        currentFilters: this.state.shopWorkOrderStatuses
          .filter((item) => value.includes(item.key))
          .map((item) => {
            return {
              key: item.key,
              label: item.name,
            };
          }),
      });
    }

    if (key === 'assignedTo') {
      this.setState({
        currentAssignedToFilters: this.state.currentAssignedToFilterOptions
          .filter((item) => value.includes(item.value))
          .map((item) => {
            return {
              key: item.value,
              label: item.text,
            };
          }),
      });
    }

    if (key === 'vendor') {
      this.setState({
        currentVendorFilters: this.state.vendors
          .filter((item) => value.includes(item.id))
          .map((item) => {
            return {
              key: item.id,
              label: item.name,
            };
          }),
      });
    }

    this.onChangeCurrentFilters();
  };

  togglePopoverVisibility = (visible) => {
    this.setState({ popoverVisible: visible });
  };

  onClearAllFilters = () => {
    this.setState(
      {
        currentFilters: [],
        currentAssignedToFilters: [],
        currentVendorFilters: [],
        currentCreatedByFilters: [],
        currentCreatedAtFilters: {
          dateRange: null,
          startDate: null,
          endDate: null,
        },
        currentStartedAtFilters: {
          dateRange: null,
          startDate: null,
          endDate: null,
        },
        currentServiceTaskFilters: {
          vmrs_sys_code: null,
          vmrs_asm_code: null,
          vmrs_comp_code: null,
          searchValue: '',
        },
        fileTransferFilters: [],
        filteredInfo: {
          status: [],
          assignedTo: [],
          vendor: [],
          fileTransfer: [],
        },
      },
      () => {
        this.onChangeCurrentFilters();
      }
    );
  };

  // New method to set up scroll synchronization
  setupScrollSync = () => {
    // Clean up any existing listeners first
    this.cleanupScrollSync();

    const bottomScrollbar = this.bottomScrollRef.current;
    const bottomContainer = this.bottomContainerRef.current;

    if (!this.tableRef.current || !bottomScrollbar || !bottomContainer) {
      return;
    }

    const tableBody = this.tableRef.current.querySelector('.ant-table-content');

    if (!tableBody) {
      return;
    }

    // Set the width of the bottom scrollbar content to match the width of the table
    this.updateBottomScrollbarWidth(
      tableBody,
      bottomScrollbar,
      bottomContainer
    );

    // Store references to elements and handlers for cleanup
    this._scrollElements = { tableBody, bottomScrollbar, bottomContainer };

    // Create event handlers
    this._handleBottomScroll = () => {
      if (tableBody) {
        tableBody.scrollLeft = bottomScrollbar.scrollLeft;
      }
    };

    this._handleTableScroll = () => {
      if (bottomScrollbar) {
        bottomScrollbar.scrollLeft = tableBody.scrollLeft;
      }
    };

    // Attach event listeners
    bottomScrollbar.addEventListener('scroll', this._handleBottomScroll);
    tableBody.addEventListener('scroll', this._handleTableScroll);

    // Create MutationObserver to watch for table changes
    this._observer = new MutationObserver(() => {
      this.updateBottomScrollbarWidth(
        tableBody,
        bottomScrollbar,
        bottomContainer
      );
    });

    // Watch for changes to the table
    this._observer.observe(tableBody, {
      childList: true,
      subtree: true,
    });
  };

  // New method to update bottom scrollbar width
  updateBottomScrollbarWidth = (
    tableBody,
    bottomScrollbar,
    bottomContainer
  ) => {
    if (!tableBody || !bottomScrollbar || !bottomContainer) return;

    const table = tableBody.querySelector('table');
    if (!table) return;

    // Add extra buffer to ensure we can scroll all the way
    const tableWidth = Math.ceil(table.offsetWidth) + 50;
    const contentElement = bottomScrollbar.querySelector(
      '.bottom-scrollbar-content'
    );
    if (contentElement) {
      contentElement.style.width = `${tableWidth}px`;
    }

    // Position the scrollbar container correctly
    const tableRect = this.tableRef?.current?.getBoundingClientRect();
    if (tableRect) {
      bottomContainer.style.width = `${tableRect.width}px`;
      const rightOffset =
        window.innerWidth - (tableRect.left + tableRect.width);
      bottomContainer.style.right = `${Math.max(rightOffset, 16)}px`;
    }
  };

  // New method to clean up scroll synchronization
  cleanupScrollSync = () => {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    if (this._scrollElements) {
      const { tableBody, bottomScrollbar } = this._scrollElements;

      if (bottomScrollbar && this._handleBottomScroll) {
        bottomScrollbar.removeEventListener('scroll', this._handleBottomScroll);
      }

      if (tableBody && this._handleTableScroll) {
        tableBody.removeEventListener('scroll', this._handleTableScroll);
      }

      this._scrollElements = null;
      this._handleBottomScroll = null;
      this._handleTableScroll = null;
    }
  };
}

export default observer(WorkOrderTable);
